<template>
    <div class="t2md-shell" :class="{ 'dark': $store.state.darkMode, 'light': !$store.state.darkMode }">

        <aside class="t2md-sidebar" :class="{ 'collapsed': !sidebarOpen }">
            <div class="sidebar-header-t2md">
                <div class="sidebar-brand">
                    <span class="brand-icon"><i class="fas fa-database"></i></span>
                    <span class="brand-text" v-show="sidebarOpen">Talk 2 My Data</span>
                </div>
                <button class="sidebar-toggle" @click="toggleSidebar" :title="sidebarOpen ? 'Collapse' : 'Expand'">
                    <i :class="sidebarOpen ? 'fas fa-chevron-left' : 'fas fa-chevron-right'"></i>
                </button>
            </div>

            <div class="sidebar-actions" v-show="sidebarOpen">
                <button class="btn-new-chat" @click="startNewChat">
                    <i class="fas fa-plus"></i><span>New Chat</span>
                </button>
            </div>
            <div class="sidebar-actions" v-show="!sidebarOpen">
                <button class="btn-new-chat-icon" @click="startNewChat" title="New Chat">
                    <i class="fas fa-plus"></i>
                </button>
            </div>

            <div class="sidebar-tabs" v-show="sidebarOpen">
                <button :class="['tab-btn', { active: activeTab === 'mine' }]" @click="activeTab = 'mine'">
                    <i class="fas fa-user me-1"></i>
                    My Chats
                </button>
                <button v-if="$store.getters.userRoles?.isAqferAdmin || $store.getters.userRoles?.isAqferManager"
                    :class="['tab-btn', { active: activeTab === 'admin' }]" @click="switchToAdmin">
                    <i class="fas fa-users me-1"></i>
                    All Chats
                </button>
            </div>

            <div class="sidebar-list" v-show="sidebarOpen && activeTab === 'mine'">
                <div class="sidebar-list-header">
                    <span class="list-count" v-if="chats">{{ chats.length }} conversation{{ chats.length !== 1 ? 's' :
                        '' }}</span>
                    <div class="list-actions">
                        <input type="number" v-model.number="chatLimit" class="limit-input" min="1" max="200"
                            title="Result limit" />
                        <button class="icon-btn" @click="fetchChatHistory(true)" :disabled="historyLoading"
                            title="Refresh">
                            <i :class="['fas', historyLoading ? 'fa-spinner fa-spin' : 'fa-rotate-right']"></i>
                        </button>
                    </div>
                </div>
                <div v-if="historyLoading && (!chats || chats.length === 0)" class="sidebar-loading"><i
                        class="fas fa-spinner fa-spin"></i></div>
                <div v-else-if="!chats || chats.length === 0" class="sidebar-empty">
                    <i class="fas fa-comment-slash"></i>
                    <p>No conversations yet</p>
                </div>
                <div v-else class="chat-list">
                    <div v-for="chat in chats" :key="chat.resourceId"
                        :class="['chat-item', { active: currentChatId === chat.resourceId }]"
                        @click="loadHistoryChat(chat)">
                        <div class="chat-item-icon"><i class="fas fa-message"></i></div>
                        <div class="chat-item-body">
                            <div class="chat-item-title">{{ chat.title || 'Untitled Chat' }}</div>
                            <div class="chat-item-meta">{{ relativeTime(chat.updatedAt || chat.createdAt) }}</div>
                        </div>
                        <button class="chat-item-delete" @click.stop="confirmDeleteChat([chat])" title="Delete"><i
                                class="fas fa-trash"></i></button>
                    </div>
                </div>
            </div>

            <div class="sidebar-list" v-show="sidebarOpen && activeTab === 'admin'">
                <div class="sidebar-list-header">
                    <span class="list-count" v-if="adminChats">{{ adminChats.length }} total</span>
                    <div class="list-actions">
                        <input type="number" v-model.number="adminChatLimitInput" class="limit-input" min="1"
                            max="500" />
                        <button class="icon-btn" @click="fetchAdminHistory(true)" :disabled="adminLoading"
                            title="Refresh">
                            <i :class="['fas', adminLoading ? 'fa-spinner fa-spin' : 'fa-rotate-right']"></i>
                        </button>
                    </div>
                </div>
                <div v-if="adminLoading && (!adminChats || adminChats.length === 0)" class="sidebar-loading"><i
                        class="fas fa-spinner fa-spin"></i></div>
                <div v-else-if="!adminChats || adminChats.length === 0" class="sidebar-empty">
                    <i class="fas fa-comment-slash"></i>
                    <p>No conversations found</p>
                </div>
                <div v-else class="chat-list">
                    <div v-for="chat in adminChats" :key="chat.resourceId"
                        :class="['chat-item', { active: currentChatId === chat.resourceId }]"
                        @click="loadHistoryChat(chat)">
                        <div class="chat-item-icon admin"><i class="fas fa-message"></i></div>
                        <div class="chat-item-body">
                            <div class="chat-item-title">{{ chat.title || 'Untitled Chat' }}</div>
                            <div class="chat-item-meta">
                                <span class="user-badge" :title="chat.userId || 'Unknown'">{{ chat.userId || 'Unknown'
                                }}</span>
                                {{ relativeTime(chat.updatedAt || chat.createdAt) }}
                            </div>
                        </div>
                        <button class="chat-item-delete" @click.stop="confirmDeleteChat([chat])" title="Delete"><i
                                class="fas fa-trash"></i></button>
                    </div>
                </div>
            </div>
        </aside>

        <main class="t2md-main">

            <header class="t2md-topbar">
                <div class="topbar-left">
                    <template v-if="viewMode === 'new'">
                        <span class="context-badge new"><i class="fas fa-sparkles"></i> New Chat</span>
                        <div class="topbar-selects ms-2" v-if="!activeStreamingChatId">
                            <div class="topbar-select-group">
                                <i class="fas fa-database topbar-select-icon"></i>
                                <select v-model="database" @change="handleDatabaseChange(database)"
                                    class="topbar-select" :disabled="streamingLoading">
                                    <option value="" disabled>Database...</option>
                                    <option v-for="opt in databaseOptions" :key="opt.value" :value="opt.value">{{
                                        opt.label }}</option>
                                </select>
                            </div>
                            <span class="topbar-select-divider" v-if="database">/</span>
                            <div class="topbar-select-group" v-if="database">
                                <i class="fas fa-table topbar-select-icon"></i>
                                <div v-if="tablesLoading" class="topbar-loading-inline"><i
                                        class="fas fa-spinner fa-spin"></i></div>
                                <select v-else v-model="table" class="topbar-select"
                                    :disabled="streamingLoading || !database">
                                    <option value="">Table...</option>
                                    <option v-for="opt in tableOptions" :key="opt.value" :value="opt.value">{{ opt.label
                                    }}</option>
                                </select>
                            </div>
                        </div>
                    </template>

                    <template v-else-if="viewMode === 'history' && currentChatId">
                        <span class="context-badge history"><i class="fas fa-clock-rotate-left"></i> History</span>
                        <span class="context-title">{{ currentChatTitle }}</span>
                    </template>
                </div>

                <div class="topbar-right">
                    <template v-if="viewMode === 'history' && currentChatId">
                        <div class="meta-chip" v-if="currentDatabase"><i class="fas fa-database"></i> {{ currentDatabase
                        }}</div>
                        <div class="meta-chip" v-if="currentTable"><i class="fas fa-table"></i> {{ currentTable }}</div>
                        <button class="icon-btn danger" @click="confirmDeleteChat([currentHistoryChat])"
                            title="Delete this chat">
                            <i class="fas fa-trash"></i>
                        </button>
                    </template>
                    <template v-else-if="viewMode == 'new' && activeStreamingChatId">
                        <div class="meta-chip" v-if="database"><i class="fas fa-database"></i> {{ database }}</div>
                        <div class="meta-chip" v-if="table"><i class="fas fa-table"></i> {{ table }}</div>
                    </template>
                </div>
            </header>

            <div v-if="viewMode === 'new'" class="chat-workspace">
                <div class="messages-container" ref="messageThread">

                    <div v-if="!activeStreamingChatId" class="empty-state">
                        <div class="empty-icon"><i class="fas fa-wand-magic-sparkles"></i></div>
                        <h2 class="empty-title">Ask your data anything</h2>
                        <p class="empty-subtitle">Select a database and table in the toolbar above, then type your
                            question.</p>
                        <div class="empty-examples">
                            <div class="example-chip" v-for="ex in exampleQuestions" :key="ex"
                                @click="table && (question = ex)">{{
                                    ex }}</div>
                        </div>
                    </div>

                    <template v-if="activeStreamingChatId">
                        <div v-for="(msg, idx) in streamingMessages" :key="idx" class="message" :class="msg.role">
                            <div class="message-avatar" :class="msg.role">
                                <i :class="msg.role === 'user' ? 'fas fa-user' : 'fas fa-robot'"></i>
                            </div>
                            <div class="message-content">
                                <template v-if="msg.role === 'user'">
                                    <div class="message-text">
                                        <div class="msg-question">{{ msg.content?.question || msg.content?.text ||
                                            msg.content }}</div>
                                    </div>
                                </template>
                                <template v-else>
                                    <div v-if="(msg.statusUpdates && msg.statusUpdates.length > 0) || (streamingLoading && isLatestAssistantMsg(idx, streamingMessages) && lastMsgIsAssistant(streamingMessages))"
                                        class="mb-2">
                                        <div class="agent-status" :class="{ collapsed: !newStatusExpanded[idx] }"
                                            role="button" @click="toggleNewStatus(idx)">

                                            <template
                                                v-if="streamingLoading && isLatestAssistantMsg(idx, streamingMessages) && lastMsgIsAssistant(streamingMessages)">
                                                <i class="fas fa-circle fa-beat-fade text-success fs-5"></i>
                                                <span class="status-text fst-italic fw-bold">{{ getCurrentAgentStatus
                                                    }}</span>
                                            </template>
                                            <template v-else>
                                                <i
                                                    :class="['fas', msg.content?.guardrail_action === 'guardrail_intervened' ? 'fa-xmark text-danger' : 'fa-check text-success']"></i>
                                                <span class="status-text fst-italic fw-bold">{{
                                                    msg.content?.guardrail_action == 'guardrail_intervened' ? 'Blocked'
                                                        : 'Done' }} - {{ msg.statusUpdates?.length || 0 }} steps</span>
                                            </template>

                                            <i v-if="msg.statusUpdates && msg.statusUpdates.length > 0"
                                                class="fas fa-angle-down collapse-arrow-down"></i>
                                            <i v-if="msg.statusUpdates && msg.statusUpdates.length > 0"
                                                class="fas fa-angle-up collapse-arrow-up"></i>
                                        </div>
                                        <div v-if="newStatusExpanded[idx] && msg.statusUpdates && msg.statusUpdates.length > 0"
                                            class="status-list">
                                            <div v-for="(s, si) in msg.statusUpdates" :key="si" class="status-row">
                                                <div class="status-icon-col"><i
                                                        :class="getAgentStatusIcon(s.label, streamingLoading && isLatestAssistantMsg(idx, streamingMessages) && currentStatus(msg.statusUpdates)?.[si])"></i>
                                                </div>
                                                <div class="status-label-col fw-bold">{{ s.label }}</div>
                                                <div class="status-detail-col">{{ s.detail }}</div>
                                                <div class="status-time-col">{{ (s.timestamp || '').replace('T',
                                                    '').replace('Z', '') }}</div>
                                            </div>
                                        </div>
                                    </div>

                                    <div v-if="msg.content?.guardrail_action === 'guardrail_intervened'"
                                        class="blocked-message">
                                        {{ msg.content.guardrail_message }}
                                    </div>
                                    <template v-else-if="msg.msgType === 'refine_query'">
                                        <div class="assistant-bubble"
                                            v-if="msg.content?.text_response || msg.content?.refined_query">
                                            <component :is="'Markdown'" v-if="msg.content?.text_response"
                                                :content="msg.content.text_response" class="qd-markdown" />
                                            <pre v-if="msg.content?.refined_query"
                                                class="qd-pre">{{ msg.content.refined_query }}</pre>

                                            <div class="qd-actions-wrap" v-if="msg.content?.refined_query">
                                                <div class="qd-actions">
                                                    <button class="qd-btn run"
                                                        :disabled="streamingLoading || !isLatestAssistantMsg(idx, streamingMessages)"
                                                        @click="triggerExecuteQueryFromRefined(msg, idx, 'new')">
                                                        <i class="fas fa-play"></i> Run Query
                                                    </button>
                                                    <button class="qd-btn refine"
                                                        :class="{ active: refineOpen[`new_rq_${idx}`] }"
                                                        :disabled="streamingLoading || !isLatestAssistantMsg(idx, streamingMessages)"
                                                        @click="toggleRefinePanel(`new_rq_${idx}`)">
                                                        <i class="fas fa-rotate"></i> Refine Again
                                                        <i
                                                            :class="['fas', 'refine-chevron', refineOpen[`new_rq_${idx}`] ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
                                                    </button>
                                                    <button class="qd-btn save"
                                                        @click="saveQuery(msg.content.refined_query)">
                                                        <i class="fas fa-bookmark"></i>
                                                    </button>
                                                </div>

                                                <div class="refine-inline-panel"
                                                    :class="{ open: refineOpen[`new_rq_${idx}`] }">
                                                    <div class="refine-panel-inner">
                                                        <div class="refine-panel-header">
                                                            <span>Custom refinement instructions</span>
                                                            <span class="refine-optional">optional - leave blank to
                                                                auto-refine</span>
                                                        </div>
                                                        <textarea :ref="`refineInput_new_rq_${idx}`"
                                                            v-model="refineText[`new_rq_${idx}`]"
                                                            class="refine-panel-textarea"
                                                            placeholder="e.g. Simplify the JOIN, add an index hint, rename columns..."
                                                            rows="2"
                                                            @keydown="handleRefineKeydown($event, `new_rq_${idx}`, msg.content.refined_query, idx, 'new')"></textarea>
                                                        <div class="refine-panel-footer">
                                                            <span class="refine-kbd-hint"><kbd>Ctrl</kbd><kbd>↵</kbd>
                                                                to
                                                                confirm · <kbd>Esc</kbd> to cancel</span>
                                                            <div class="refine-panel-actions">
                                                                <button class="refine-panel-btn cancel"
                                                                    @click="closeRefinePanel(`new_rq_${idx}`)">Cancel</button>
                                                                <button class="refine-panel-btn confirm"
                                                                    :disabled="streamingLoading"
                                                                    @click="submitRefine(`new_rq_${idx}`, msg.content.refined_query, idx, 'new')">
                                                                    <i class="fas fa-wand-magic-sparkles"></i> Refine
                                                                    Query
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </template>
                                    <template v-else-if="msg.msgType === 'generate_query'">
                                        <div class="assistant-bubble"
                                            v-if="msg.content?.text_response || msg.content?.athena_query">
                                            <component :is="'Markdown'" v-if="msg.content?.text_response"
                                                :content="msg.content.text_response" class="qd-markdown" />
                                            <pre v-if="msg.content?.athena_query"
                                                class="qd-pre">{{ msg.content.athena_query }}</pre>
                                            <div class="qd-actions-wrap" v-if="msg.content?.athena_query">
                                                <div class="qd-actions">
                                                    <button class="qd-btn run"
                                                        :disabled="streamingLoading || !isLatestAssistantMsg(idx, streamingMessages)"
                                                        @click="triggerExecuteQuery(msg, idx, 'new')">
                                                        <i class="fas fa-play"></i> Run Query
                                                    </button>
                                                    <button class="qd-btn refine"
                                                        :class="{ active: refineOpen[`new_gq_${idx}`] }"
                                                        :disabled="streamingLoading || !isLatestAssistantMsg(idx, streamingMessages)"
                                                        @click="toggleRefinePanel(`new_gq_${idx}`)">
                                                        <i class="fas fa-wand-magic-sparkles"></i> Refine
                                                        <i
                                                            :class="['fas', 'refine-chevron', refineOpen[`new_gq_${idx}`] ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
                                                    </button>
                                                    <button class="qd-btn save"
                                                        @click="saveQuery(msg.content.athena_query)">
                                                        <i class="fas fa-bookmark"></i>
                                                    </button>
                                                </div>

                                                <div class="refine-inline-panel"
                                                    :class="{ open: refineOpen[`new_gq_${idx}`] }">
                                                    <div class="refine-panel-inner">
                                                        <div class="refine-panel-header">
                                                            <span>Custom refinement instructions</span>
                                                            <span class="refine-optional">optional - leave blank to
                                                                auto-refine</span>
                                                        </div>
                                                        <textarea :ref="`refineInput_new_gq_${idx}`"
                                                            v-model="refineText[`new_gq_${idx}`]"
                                                            class="refine-panel-textarea"
                                                            placeholder="e.g. Add a WHERE clause for last 30 days, GROUP BY region, LIMIT 10..."
                                                            rows="2"
                                                            @keydown="handleRefineKeydown($event, `new_gq_${idx}`, msg.content.athena_query, idx, 'new')"></textarea>
                                                        <div class="refine-panel-footer">
                                                            <span class="refine-kbd-hint"><kbd>Ctrl</kbd><kbd>↵</kbd>
                                                                to
                                                                confirm · <kbd>Esc</kbd> to cancel</span>
                                                            <div class="refine-panel-actions">
                                                                <button class="refine-panel-btn cancel"
                                                                    @click="closeRefinePanel(`new_gq_${idx}`)">Cancel</button>
                                                                <button class="refine-panel-btn confirm"
                                                                    :disabled="streamingLoading"
                                                                    @click="submitRefine(`new_gq_${idx}`, msg.content.athena_query, idx, 'new')">
                                                                    <i class="fas fa-wand-magic-sparkles"></i> Refine
                                                                    Query
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </template>
                                    <template v-else-if="msg.msgType === 'execute_query'">
                                        <div class="assistant-bubble"
                                            v-if="msg.content?.final_answer || msg.content?.query_result || msg.content?.adjusted_query">
                                            <div v-if="msg.content?.query_result" class="qd-results mt-0">
                                                <div class="qd-section-label"><i class="fas fa-table-list"></i> Query
                                                    Results</div>
                                                <div v-if="msg.content.query_result.error" class="qd-error">{{
                                                    msg.content.query_result.error }}</div>
                                                <div v-else class="qd-table-wrap">
                                                    <table class="qd-table">
                                                        <thead>
                                                            <tr>
                                                                <th v-for="col in msg.content.query_result.column_info"
                                                                    :key="col.name">{{ col.name }}</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            <tr v-for="(row, ri) in msg.content.query_result.data"
                                                                :key="ri">
                                                                <td v-for="(col, ci) in msg.content.query_result.column_info"
                                                                    :key="ci">{{ row[ci] }}</td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                            <div v-if="msg.content?.final_answer" class="qd-analysis">
                                                <div class="qd-section-label"><i class="fas fa-chart-line"></i> Analysis
                                                </div>
                                                <component :is="'Markdown'" :content="msg.content.final_answer"
                                                    class="qd-markdown" />
                                                <template v-if="msg.content?.adjusted_query">
                                                    <pre class="qd-pre">{{ msg.content.adjusted_query }}</pre>
                                                    <div class="qd-actions-wrap" v-if="msg.content?.adjusted_query">
                                                        <div class="qd-actions">
                                                            <button class="qd-btn run"
                                                                :disabled="streamingLoading || !isLatestAssistantMsg(idx, streamingMessages)"
                                                                @click="triggerExecuteQueryFromAdjusted(msg, idx, 'new')">
                                                                <i class="fas fa-play"></i> Run Query
                                                            </button>
                                                            <button class="qd-btn refine"
                                                                :class="{ active: refineOpen[`new_gq_${idx}`] }"
                                                                :disabled="streamingLoading || !isLatestAssistantMsg(idx, streamingMessages)"
                                                                @click="toggleRefinePanel(`new_gq_${idx}`)">
                                                                <i class="fas fa-wand-magic-sparkles"></i> Refine
                                                                <i
                                                                    :class="['fas', 'refine-chevron', refineOpen[`new_gq_${idx}`] ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
                                                            </button>
                                                            <button class="qd-btn save"
                                                                @click="saveQuery(msg.content.adjusted_query)">
                                                                <i class="fas fa-bookmark"></i>
                                                            </button>
                                                        </div>

                                                        <div class="refine-inline-panel"
                                                            :class="{ open: refineOpen[`new_gq_${idx}`] }">
                                                            <div class="refine-panel-inner">
                                                                <div class="refine-panel-header">
                                                                    <span>Custom refinement instructions</span>
                                                                    <span class="refine-optional">optional - leave
                                                                        blank
                                                                        to auto-refine</span>
                                                                </div>
                                                                <textarea :ref="`refineInput_new_gq_${idx}`"
                                                                    v-model="refineText[`new_gq_${idx}`]"
                                                                    class="refine-panel-textarea"
                                                                    placeholder="e.g. Add a WHERE clause for last 30 days, GROUP BY region, LIMIT 10..."
                                                                    rows="2"
                                                                    @keydown="handleRefineKeydown($event, `new_gq_${idx}`, msg.content.adjusted_query, idx, 'new')"></textarea>
                                                                <div class="refine-panel-footer">
                                                                    <span
                                                                        class="refine-kbd-hint"><kbd>Ctrl</kbd><kbd>↵</kbd>
                                                                        to confirm · <kbd>Esc</kbd> to
                                                                        cancel</span>
                                                                    <div class="refine-panel-actions">
                                                                        <button class="refine-panel-btn cancel"
                                                                            @click="closeRefinePanel(`new_gq_${idx}`)">Cancel</button>
                                                                        <button class="refine-panel-btn confirm"
                                                                            :disabled="streamingLoading"
                                                                            @click="submitRefine(`new_gq_${idx}`, msg.content.adjusted_query, idx, 'new')">
                                                                            <i class="fas fa-wand-magic-sparkles"></i>
                                                                            Refine Query
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </template>
                                            </div>
                                        </div>
                                    </template>
                                    <template v-else>
                                        <div class="assistant-bubble"
                                            v-if="msg.content?.text_response || msg.content?.athena_query">
                                            <component :is="'Markdown'" v-if="msg.content?.text_response"
                                                :content="msg.content.text_response" class="qd-markdown" />
                                            <pre v-if="msg.content?.athena_query"
                                                class="qd-pre">{{ msg.content.athena_query }}</pre>
                                        </div>
                                    </template>
                                    <div v-if="msg.isResponseCancelled == true"
                                        class="stopped-message text-danger fs-5 mt-1">
                                        <i class="fas fa-ban"></i> Response stopped.
                                    </div>
                                </template>
                            </div>
                        </div>

                        <div v-if="streamingLoading && !lastMsgIsAssistant(streamingMessages)"
                            class="message assistant">
                            <div class="message-avatar assistant"><i class="fas fa-robot"></i></div>
                            <div class="message-content">
                                <div v-if="agentStatusList.length > 0 || agentStatus" class="mb-2">
                                    <div class="agent-status" :class="{ collapsed: !agentStatusExpanded }" role="button"
                                        @click="agentStatusExpanded = !agentStatusExpanded">
                                        <i class="fas fa-circle fa-beat-fade text-success fs-5"></i>
                                        <span class="status-text fst-italic fw-bold">{{ getCurrentAgentStatus }}</span>
                                        <i v-if="agentStatusList.length > 0"
                                            class="fas fa-angle-down collapse-arrow-down"></i>
                                        <i v-if="agentStatusList.length > 0"
                                            class="fas fa-angle-up collapse-arrow-up"></i>
                                    </div>
                                    <div v-if="agentStatusExpanded && agentStatusList.length > 0" class="status-list">
                                        <div v-for="(s, i) in agentStatusList" :key="i" class="status-row">
                                            <div class="status-icon-col"><i
                                                    :class="getAgentStatusIcon(s.label, streamingLoading && currentStatus(agentStatusList)?.[i])"></i>
                                            </div>
                                            <div class="status-label-col fw-bold">{{ s.label }}</div>
                                            <div class="status-detail-col">{{ s.detail }}</div>
                                            <div class="status-time-col">{{ s.timestamp }}</div>
                                        </div>
                                    </div>
                                </div>
                                <div class="streaming-indicator">
                                    <span class="dot"></span><span class="dot"></span><span class="dot"></span>
                                </div>
                            </div>
                        </div>
                    </template>
                </div>

                <div class="input-bar-wrapper">
                    <div class="input-bar">
                        <textarea ref="questionInput" v-model="question" :disabled="streamingLoading || !database"
                            :placeholder="table ? `Ask about ${table}...` : database ? 'Ask any question' : 'Select a database and table first'"
                            class="question-textarea" :class="{ 'textarea-disabled': !database || streamingLoading }"
                            rows="1" @keydown.enter.exact.prevent="handleQuestionSubmit" @input="autoResize"></textarea>
                        <button v-if="streamingLoading"
                            :class="['send-btn', aborting ? 'disabled-bg-danger' : 'bg-danger']" :disabled="aborting"
                            @click="abortStream" title="Stop generating">
                            <i v-if="aborting" class="fas fa-spinner fa-spin"></i>
                            <i v-else class="fas fa-stop"></i>
                        </button>
                        <button v-else
                            :class="['send-btn', (!question.trim() || !database) ? 'disabled-bg-primary' : 'bg-primary']"
                            :disabled="!question.trim() || !database" @click="handleQuestionSubmit">
                            <i class="fas fa-paper-plane"></i>
                        </button>
                    </div>
                    <p class="input-disclaimer">This tool uses AI, which can make mistakes. Verify important outputs
                        before acting on
                        them.</p>
                </div>
            </div>

            <div v-else-if="viewMode === 'history'" class="chat-workspace">
                <div class="messages-container" ref="historyThread">
                    <div v-if="messagesLoading" class="loading-state">
                        <i class="fas fa-spinner fa-spin"></i>
                        <p>Loading conversation...</p>
                    </div>
                    <div v-else-if="!currentMessages || currentMessages.length === 0" class="empty-state">
                        <div class="empty-icon"><i class="fas fa-comment-slash"></i></div>
                        <p class="empty-subtitle">No messages found in this conversation.</p>
                    </div>
                    <template v-else>
                        <div v-for="(msg, idx) in currentMessages" :key="idx" class="message" :class="msg.role">
                            <div class="message-avatar" :class="msg.role">
                                <i :class="msg.role === 'user' ? 'fas fa-user' : 'fas fa-robot'"></i>
                            </div>
                            <div class="message-content">
                                <template v-if="msg.role === 'user'">
                                    <div class="message-text">
                                        <div class="msg-question">{{ msg.content?.question || msg.content?.text ||
                                            msg.content }}</div>
                                        <div class="msg-context-tags" v-if="msg.content?.db_name">
                                            <span class="tag"><i class="fas fa-database"></i> {{ msg.content.db_name
                                            }}</span>
                                            <span class="tag" v-if="msg?.content?.table_name"><i class="fas fa-table"></i> {{ msg.content.table_name
                                            }}</span>
                                        </div>
                                    </div>
                                </template>
                                <template v-else>
                                    <div v-if="(msg.statusUpdates && msg.statusUpdates.length > 0) || (streamingLoading && isLatestAssistantMsg(idx, currentMessages) && lastMsgIsAssistant(currentMessages))"
                                        class="mb-2">
                                        <div class="agent-status" :class="{ collapsed: !historyStatusExpanded[idx] }"
                                            role="button" @click="toggleHistoryStatus(idx)">

                                            <template
                                                v-if="streamingLoading && isLatestAssistantMsg(idx, currentMessages) && lastMsgIsAssistant(currentMessages)">
                                                <i class="fas fa-circle fa-beat-fade text-success fs-5"></i>
                                                <span class="status-text fst-italic fw-bold">{{ getCurrentAgentStatus
                                                    }}</span>
                                            </template>
                                            <template v-else>
                                                <i
                                                    :class="['fas', msg.content?.guardrail_action === 'guardrail_intervened' ? 'fa-xmark text-danger' : 'fa-check text-success']"></i>
                                                <span class="status-text fst-italic fw-bold">{{
                                                    msg.content?.guardrail_action == 'guardrail_intervened' ? 'Blocked'
                                                        : 'Done' }} - {{ msg.statusUpdates?.length || 0 }} steps</span>
                                            </template>

                                            <i v-if="msg.statusUpdates && msg.statusUpdates.length > 0"
                                                class="fas fa-angle-down collapse-arrow-down"></i>
                                            <i v-if="msg.statusUpdates && msg.statusUpdates.length > 0"
                                                class="fas fa-angle-up collapse-arrow-up"></i>
                                        </div>
                                        <div v-if="historyStatusExpanded[idx] && msg.statusUpdates && msg.statusUpdates.length > 0"
                                            class="status-list">
                                            <div v-for="(s, si) in msg.statusUpdates" :key="si" class="status-row">
                                                <div class="status-icon-col"><i
                                                        :class="getAgentStatusIcon(s.label, streamingLoading && isLatestAssistantMsg(idx, currentMessages) && currentStatus(msg.statusUpdates)?.[si])"></i>
                                                </div>
                                                <div class="status-label-col fw-bold">{{ s.label }}</div>
                                                <div class="status-detail-col">{{ s.detail }}</div>
                                                <div class="status-time-col">{{ (s.timestamp || '').replace('T',
                                                    '').replace('Z', '') }}</div>
                                            </div>
                                        </div>
                                    </div>

                                    <div v-if="msg.content?.guardrail_action === 'guardrail_intervened'"
                                        class="blocked-message">
                                        {{ msg.content.guardrail_message }}
                                    </div>
                                    <template v-else-if="isRefineQueryMsg(msg)">
                                        <div class="assistant-bubble"
                                            v-if="msg.content?.text_response || msg.content?.refined_query">
                                            <component :is="'Markdown'" v-if="msg.content?.text_response"
                                                :content="msg.content.text_response" class="qd-markdown" />
                                            <pre v-if="msg.content?.refined_query"
                                                class="qd-pre">{{ msg.content.refined_query }}</pre>

                                            <div class="qd-actions-wrap" v-if="msg.content?.refined_query">
                                                <div class="qd-actions">
                                                    <button class="qd-btn run"
                                                        :disabled="streamingLoading || !isLatestAssistantMsg(idx, currentMessages)"
                                                        @click="triggerExecuteQueryFromRefined(msg, idx, 'history')">
                                                        <i class="fas fa-play"></i> Run Query
                                                    </button>
                                                    <button class="qd-btn refine"
                                                        :class="{ active: refineOpen[`new_rq_${idx}`] }"
                                                        :disabled="streamingLoading || !isLatestAssistantMsg(idx, currentMessages)"
                                                        @click="toggleRefinePanel(`new_rq_${idx}`)">
                                                        <i class="fas fa-rotate"></i> Refine Again
                                                        <i
                                                            :class="['fas', 'refine-chevron', refineOpen[`new_rq_${idx}`] ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
                                                    </button>
                                                    <button class="qd-btn save"
                                                        @click="saveQuery(msg.content.refined_query)">
                                                        <i class="fas fa-bookmark"></i>
                                                    </button>
                                                </div>

                                                <div class="refine-inline-panel"
                                                    :class="{ open: refineOpen[`new_rq_${idx}`] }">
                                                    <div class="refine-panel-inner">
                                                        <div class="refine-panel-header">
                                                            <span>Custom refinement instructions</span>
                                                            <span class="refine-optional">optional - leave blank to
                                                                auto-refine</span>
                                                        </div>
                                                        <textarea :ref="`refineInput_new_rq_${idx}`"
                                                            v-model="refineText[`new_rq_${idx}`]"
                                                            class="refine-panel-textarea"
                                                            placeholder="e.g. Simplify the JOIN, add an index hint, rename columns..."
                                                            rows="2"
                                                            @keydown="handleRefineKeydown($event, `new_rq_${idx}`, msg.content.refined_query, idx, 'history')"></textarea>
                                                        <div class="refine-panel-footer">
                                                            <span class="refine-kbd-hint"><kbd>Ctrl</kbd><kbd>↵</kbd>
                                                                to
                                                                confirm · <kbd>Esc</kbd> to cancel</span>
                                                            <div class="refine-panel-actions">
                                                                <button class="refine-panel-btn cancel"
                                                                    @click="closeRefinePanel(`new_rq_${idx}`)">Cancel</button>
                                                                <button class="refine-panel-btn confirm"
                                                                    :disabled="streamingLoading"
                                                                    @click="submitRefine(`new_rq_${idx}`, msg.content.refined_query, idx, 'history')">
                                                                    <i class="fas fa-wand-magic-sparkles"></i> Refine
                                                                    Query
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </template>
                                    <template v-else-if="isGenerateQueryMsg(msg, idx)">
                                        <div class="assistant-bubble"
                                            v-if="msg.content?.text_response || msg.content?.athena_query">
                                            <component :is="'Markdown'" v-if="msg.content?.text_response"
                                                :content="msg.content.text_response" class="qd-markdown" />
                                            <pre v-if="msg.content?.athena_query"
                                                class="qd-pre">{{ msg.content.athena_query }}</pre>
                                            <div class="qd-actions-wrap" v-if="msg.content?.athena_query">
                                                <div class="qd-actions">
                                                    <button class="qd-btn run"
                                                        :disabled="streamingLoading || !isLatestAssistantMsg(idx, currentMessages)"
                                                        @click="triggerExecuteQuery(msg, idx, 'history')">
                                                        <i class="fas fa-play"></i> Run Query
                                                    </button>
                                                    <button class="qd-btn refine"
                                                        :class="{ active: refineOpen[`new_gq_${idx}`] }"
                                                        :disabled="streamingLoading || !isLatestAssistantMsg(idx, currentMessages)"
                                                        @click="toggleRefinePanel(`new_gq_${idx}`)">
                                                        <i class="fas fa-wand-magic-sparkles"></i> Refine
                                                        <i
                                                            :class="['fas', 'refine-chevron', refineOpen[`new_gq_${idx}`] ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
                                                    </button>
                                                    <button class="qd-btn save"
                                                        @click="saveQuery(msg.content.athena_query)">
                                                        <i class="fas fa-bookmark"></i>
                                                    </button>
                                                </div>

                                                <div class="refine-inline-panel"
                                                    :class="{ open: refineOpen[`new_gq_${idx}`] }">
                                                    <div class="refine-panel-inner">
                                                        <div class="refine-panel-header">
                                                            <span>Custom refinement instructions</span>
                                                            <span class="refine-optional">optional - leave blank to
                                                                auto-refine</span>
                                                        </div>
                                                        <textarea :ref="`refineInput_new_gq_${idx}`"
                                                            v-model="refineText[`new_gq_${idx}`]"
                                                            class="refine-panel-textarea"
                                                            placeholder="e.g. Add a WHERE clause for last 30 days, GROUP BY region, LIMIT 10..."
                                                            rows="2"
                                                            @keydown="handleRefineKeydown($event, `new_gq_${idx}`, msg.content.athena_query, idx, 'history')"></textarea>
                                                        <div class="refine-panel-footer">
                                                            <span class="refine-kbd-hint"><kbd>Ctrl</kbd><kbd>↵</kbd>
                                                                to
                                                                confirm · <kbd>Esc</kbd> to cancel</span>
                                                            <div class="refine-panel-actions">
                                                                <button class="refine-panel-btn cancel"
                                                                    @click="closeRefinePanel(`new_gq_${idx}`)">Cancel</button>
                                                                <button class="refine-panel-btn confirm"
                                                                    :disabled="streamingLoading"
                                                                    @click="submitRefine(`new_gq_${idx}`, msg.content.athena_query, idx, 'history')">
                                                                    <i class="fas fa-wand-magic-sparkles"></i> Refine
                                                                    Query
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </template>
                                    <template v-else-if="isExecuteQueryMsg(msg)">
                                        <div class="assistant-bubble"
                                            v-if="msg.content?.final_answer || msg.content?.query_result || msg.content?.adjusted_query">
                                            <div v-if="msg.content?.query_result" class="qd-results mt-0">
                                                <div class="qd-section-label"><i class="fas fa-table-list"></i> Query
                                                    Results</div>
                                                <div v-if="msg.content.query_result.error" class="qd-error">{{
                                                    msg.content.query_result.error }}</div>
                                                <div v-else class="qd-table-wrap">
                                                    <table class="qd-table">
                                                        <thead>
                                                            <tr>
                                                                <th v-for="col in msg.content.query_result.column_info"
                                                                    :key="col.name">{{ col.name }}</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            <tr v-for="(row, ri) in msg.content.query_result.data"
                                                                :key="ri">
                                                                <td v-for="(col, ci) in msg.content.query_result.column_info"
                                                                    :key="ci">{{ row[ci] }}</td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                            <div v-if="msg.content?.final_answer" class="qd-analysis">
                                                <div class="qd-section-label"><i class="fas fa-chart-line"></i> Analysis
                                                </div>
                                                <component :is="'Markdown'" :content="msg.content.final_answer"
                                                    class="qd-markdown" />
                                                <template v-if="msg.content?.adjusted_query">
                                                    <pre class="qd-pre">{{ msg.content.adjusted_query }}</pre>
                                                    <div class="qd-actions-wrap">
                                                        <div class="qd-actions">
                                                            <button class="qd-btn run"
                                                                :disabled="streamingLoading || !isLatestAssistantMsg(idx, currentMessages)"
                                                                @click="triggerExecuteQueryFromAdjusted(msg, idx, 'history')">
                                                                <i class="fas fa-play"></i> Run Query
                                                            </button>
                                                            <button class="qd-btn refine"
                                                                :class="{ active: refineOpen[`new_gq_${idx}`] }"
                                                                :disabled="streamingLoading || !isLatestAssistantMsg(idx, currentMessages)"
                                                                @click="toggleRefinePanel(`new_gq_${idx}`)">
                                                                <i class="fas fa-wand-magic-sparkles"></i> Refine
                                                                <i
                                                                    :class="['fas', 'refine-chevron', refineOpen[`new_gq_${idx}`] ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
                                                            </button>
                                                            <button class="qd-btn save"
                                                                @click="saveQuery(msg.content.adjusted_query)">
                                                                <i class="fas fa-bookmark"></i>
                                                            </button>
                                                        </div>

                                                        <div class="refine-inline-panel"
                                                            :class="{ open: refineOpen[`new_gq_${idx}`] }">
                                                            <div class="refine-panel-inner">
                                                                <div class="refine-panel-header">
                                                                    <span>Custom refinement instructions</span>
                                                                    <span class="refine-optional">optional - leave
                                                                        blank
                                                                        to auto-refine</span>
                                                                </div>
                                                                <textarea :ref="`refineInput_new_gq_${idx}`"
                                                                    v-model="refineText[`new_gq_${idx}`]"
                                                                    class="refine-panel-textarea"
                                                                    placeholder="e.g. Add a WHERE clause for last 30 days, GROUP BY region, LIMIT 10..."
                                                                    rows="2"
                                                                    @keydown="handleRefineKeydown($event, `new_gq_${idx}`, msg.content.adjusted_query, idx, 'history')"></textarea>
                                                                <div class="refine-panel-footer">
                                                                    <span
                                                                        class="refine-kbd-hint"><kbd>Ctrl</kbd><kbd>↵</kbd>
                                                                        to confirm · <kbd>Esc</kbd> to
                                                                        cancel</span>
                                                                    <div class="refine-panel-actions">
                                                                        <button class="refine-panel-btn cancel"
                                                                            @click="closeRefinePanel(`new_gq_${idx}`)">Cancel</button>
                                                                        <button class="refine-panel-btn confirm"
                                                                            :disabled="streamingLoading"
                                                                            @click="submitRefine(`new_gq_${idx}`, msg.content.adjusted_query, idx, 'history')">
                                                                            <i class="fas fa-wand-magic-sparkles"></i>
                                                                            Refine Query
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </template>
                                            </div>
                                        </div>
                                    </template>
                                    <template v-else>
                                        <div class="assistant-bubble"
                                            v-if="msg.content?.text_response || msg.content?.athena_query">
                                            <component :is="'Markdown'" v-if="msg.content?.text_response"
                                                :content="msg.content.text_response" class="qd-markdown" />
                                            <pre v-if="msg.content?.athena_query"
                                                class="qd-pre">{{ msg.content.athena_query }}</pre>
                                        </div>
                                    </template>
                                    <div v-if="msg.isResponseCancelled == true"
                                        class="stopped-message text-danger fs-5 mt-1">
                                        <i class="fas fa-ban"></i> Response stopped.
                                    </div>
                                </template>
                            </div>
                        </div>

                        <div v-if="streamingLoading && !lastMsgIsAssistant(currentMessages)" class="message assistant">
                            <div class="message-avatar assistant"><i class="fas fa-robot"></i></div>
                            <div class="message-content">
                                <div v-if="agentStatusList.length > 0 || agentStatus" class="mb-2">
                                    <div class="agent-status" :class="{ collapsed: !agentStatusExpanded }" role="button"
                                        @click="agentStatusExpanded = !agentStatusExpanded">
                                        <i class="fas fa-circle fa-beat-fade text-success fs-5"></i>
                                        <span class="status-text fst-italic fw-bold">{{ getCurrentAgentStatus }}</span>
                                        <i v-if="agentStatusList.length > 0"
                                            class="fas fa-angle-down collapse-arrow-down"></i>
                                        <i v-if="agentStatusList.length > 0"
                                            class="fas fa-angle-up collapse-arrow-up"></i>
                                    </div>
                                    <div v-if="agentStatusExpanded && agentStatusList.length > 0" class="status-list">
                                        <div v-for="(s, i) in agentStatusList" :key="i" class="status-row">
                                            <div class="status-icon-col"><i
                                                    :class="getAgentStatusIcon(s.label, streamingLoading && currentStatus(agentStatusList)?.[i])"></i>
                                            </div>
                                            <div class="status-label-col fw-bold">{{ s.label }}</div>
                                            <div class="status-detail-col">{{ s.detail }}</div>
                                            <div class="status-time-col">{{ s.timestamp }}</div>
                                        </div>
                                    </div>
                                </div>
                                <div class="streaming-indicator">
                                    <span class="dot"></span><span class="dot"></span><span class="dot"></span>
                                </div>
                            </div>
                        </div>
                    </template>
                </div>

                <div class="input-bar-wrapper" v-if="canContinueHistory">
                    <div class="input-bar">
                        <textarea ref="continueInput" v-model="question" :disabled="streamingLoading"
                            placeholder="Ask a follow-up question..." class="question-textarea"
                            :class="{ 'textarea-disabled': streamingLoading }" rows="1"
                            @keydown.enter.exact.prevent="handleContinueSubmit" @input="autoResize"></textarea>
                        <button v-if="streamingLoading"
                            :class="['send-btn', aborting ? 'disabled-bg-danger' : 'bg-danger']" :disabled="aborting"
                            @click="abortStream" title="Stop generating">
                            <i v-if="aborting" class="fas fa-spinner fa-spin"></i>
                            <i v-else class="fas fa-stop"></i>
                        </button>
                        <button v-else
                            :class="['send-btn', (!question.trim() || !currentDatabase) ? 'disabled-bg-primary' : 'bg-primary']"
                            :disabled="!question.trim() || !currentDatabase" @click="handleContinueSubmit">
                            <i class="fas fa-paper-plane"></i>
                        </button>
                    </div>
                    <p class="input-disclaimer">This tool uses AI, which can make mistakes. Verify important outputs
                        before acting on
                        them.</p>
                </div>
            </div>

        </main>
    </div>
</template>

<script>
import Markdown from "../../components/Markdown.vue";
import axios from 'axios';
import { mapActions, mapState } from 'vuex';
import { nextTick } from 'vue';
import untruncateJson from "untruncate-json";
import { v4 as uuidv4 } from 'uuid';
import { mergeAgentsQueryIntoUrl } from '@/utils/agentsApi';
import { registerAgentStream, releaseAgentStream } from '@/utils/agentStreamGuard';
import { getAgentStatusIcon, currentStatus, DateTimeFormat } from '@/utils/commonFunction';
import dayjs from 'dayjs';
import relativeTimePlugin from 'dayjs/plugin/relativeTime';
dayjs.extend(relativeTimePlugin);

export default {
    name: "Talk2MyData",
    components: { Markdown },

    data() {
        return {
            sidebarOpen: true,
            activeTab: 'mine',
            viewMode: 'new',
            abortController: null,
            aborting: false, // prevents rapid double-click on stop

            // New-chat context
            database: "",
            table: "",
            question: "",
            databaseOptions: [],
            tableOptions: [],
            tablesLoading: false,

            // Streaming state (shared across generate & execute)
            streamingLoading: false,
            activeStreamingChatId: null,
            // Single unified message list for new-chat mode
            streamingMessages: [],
            guardrailBlocked: false,
            guardrailMessage: "",
            agentStatus: null,
            agentStatusList: [],
            agentStatusExpanded: false,

            // Status accordion expansion maps
            newStatusExpanded: {},       // keyed by message index in streamingMessages
            historyStatusExpanded: {},   // keyed by message index in currentMessages

            // History
            currentHistoryChat: null,
            currentMessages: null,   // unified list for history mode
            messagesLoading: false,
            currentChatId: null,

            // Sidebar
            historyLoading: false,
            adminLoading: false,
            chatLimit: 50,
            adminChatLimitInput: 100,

            exampleQuestions: [
                "What were the top 5 campaigns last month?",
                "Show me total revenue by region",
                "Which products had the most returns?",
                "Summarize daily active users for the past week",
            ],

            refineOpen: {},       // { [msgKey]: true/false }
            refineText: {},       // { [msgKey]: '' }  - custom instructions per message

            dbInfo: {},
        };
    },

    computed: {
        ...mapState({
            currentClient: state => state.currentClient,
            accessToken: state => state.accessToken,
            chats: state => state.amdp.t2mdChats,
            adminChats: state => state.amdp.t2mdAdminChats,
            storedChatLimit: state => state.amdp.t2mdChatLimit,
            storedAdminChatLimit: state => state.amdp.t2mdAdminChatLimit,
        }),
        getCurrentAgentStatus() {
            if (this.guardrailBlocked) return "Blocked by guardrails";
            if (this.agentStatus) return `${this.agentStatus}...`;
            if (this.streamingLoading) return "Processing...";
            return this.agentStatusList.length > 0 ? `Done - ${this.agentStatusList.length} steps` : "Done";
        },
        currentDatabase() {
            return this.currentMessages?.[0]?.content?.db_name || "";
        },
        currentTable() {
            return this.currentMessages?.[0]?.content?.table_name || "";
        },
        currentChatTitle() {
            return this.currentHistoryChat?.title || "Untitled Chat";
        },
        canContinueHistory() {
            return this.viewMode === 'history' && this.currentHistoryChat && this.currentDatabase;
        },
    },

    methods: {
        ...mapActions({
            showModal: "showModal",
            createChat: "amdp/createChat",
            upsertChatMessage: "amdp/upsertChatMessage",
            updateChatTitle: "amdp/updateChatTitle",
            getChats: "amdp/getChats",
            getChatMessages: "amdp/getChatMessages",
        }),
        untruncateJson,
        getAgentStatusIcon,
        currentStatus,
        DateTimeFormat,

        // --- Helpers ------------------------------------------------
        toggleSidebar() { this.sidebarOpen = !this.sidebarOpen; },
        autoResize(e) {
            const el = e.target;
            el.style.height = 'auto';
            el.style.height = Math.min(el.scrollHeight, 180) + 'px';
        },
        relativeTime(ts) { return ts ? dayjs(ts).fromNow() : ''; },
        toggleHistoryStatus(idx) {
            this.historyStatusExpanded = { ...this.historyStatusExpanded, [idx]: !this.historyStatusExpanded[idx] };
        },
        toggleNewStatus(idx) {
            this.newStatusExpanded = { ...this.newStatusExpanded, [idx]: !this.newStatusExpanded[idx] };
        },

        /**
         * Returns true if the assistant message at `idx` in `messageList`
         * is the LAST assistant message in that list.
         * Only the latest assistant message has its Run Query button enabled.
         */
        isLatestAssistantMsg(idx, messageList) {
            if (!messageList) return false;
            for (let i = messageList.length - 1; i >= 0; i--) {
                if (messageList[i].role === 'assistant') {
                    return i === idx;
                }
            }
            return false;
        },

        lastMsgIsAssistant(messageList) {
            if (!messageList || messageList.length === 0) return false;
            return messageList[messageList.length - 1].role === 'assistant';
        },

        // Message type helpers for history view
        isGenerateQueryMsg(msg) {
            return msg.role === 'assistant' && !msg.content?.final_answer && (msg.content?.text_response || msg.content?.athena_query);
        },
        isExecuteQueryMsg(msg) {
            return msg.role === 'assistant' && (msg.content?.final_answer || msg.content?.query_result);
        },

        isRefineQueryMsg(msg) {
            return msg.role === 'assistant' && (msg.content?.refined_query || (msg.content?.text_response && !msg.content?.athena_query && !msg.content?.final_answer));
        },

        // --- Abort / Stop stream ----------------------------------------
        abortStream() {
            if (this.aborting) return;
            this.aborting = true;
            try {
                if (this.abortController) {
                    this.abortController.abort();
                    this.abortController = null;
                }
                const messages = this.viewMode === 'new' ? this.streamingMessages : this.currentMessages;
                const message = messages.slice().reverse().find(m => m.role === 'user');
                if (!message) {
                    console.error('No latest user message found to abort stream...');
                    throw new Error('No message found to abort stream. Please contact support');
                }

                axios.post(
                    `${this.$store.state.agentsUrl}/v1/cids/${this.currentClient.cid}/talk_to_my_data/cancel`,
                    {
                        cid: this.currentClient.cid,
                        resourceId: message.resourceId,
                        messageId: message.messageId,
                    },
                    {
                        headers: {
                            'Authorization': `Bearer ${this.accessToken}`,
                        },
                    }
                );

                this.$store.dispatch('addToast', { type: 'info', message: 'Generation stopped.' });
            } catch (err) {
                console.error('Failed to set cancellation flag to backend:', err);
                this.$store.dispatch('addToast', {
                    type: 'error',
                    message: 'Failed to stop generation. Please contact support.',
                });
            } finally {
                this.aborting = false;
            }
        },

        // --- New chat ------------------------------------------------------------
        startNewChat() {
            this.viewMode = 'new';
            this.currentHistoryChat = null;
            this.currentChatId = null;
            this.currentMessages = null;
            this.activeStreamingChatId = null;
            this.streamingMessages = [];
            this.question = '';
            this.guardrailBlocked = false;
            this.guardrailMessage = '';
            this.agentStatusList = [];
            this.agentStatus = null;
            this.newStatusExpanded = {};
        },

        // --- Load history chat ------------------------------------------------------------
        async loadHistoryChat(chat) {
            if (this.currentChatId === chat.resourceId) return;
            this.viewMode = 'history';
            this.currentHistoryChat = chat;
            this.currentChatId = chat.resourceId;
            this.currentMessages = null;
            this.question = '';
            this.historyStatusExpanded = {};
            this.messagesLoading = true;
            try {
                const resp = await this.getChatMessages({ feature: "talk_to_my_data", resourceId: chat.resourceId });
                this.currentMessages = resp.data || [];

                // Now load the metadata for the first message to get the db/table context
                if (this.currentMessages.length > 0) {
                    const db = this.currentDatabase;
                    const tbl = this.currentTable;
                    if (db) {
                        let queryParams = `?database=${encodeURIComponent(db)}`;
                        if (tbl) queryParams += `&table=${encodeURIComponent(tbl)}`;
                        axios.get(
                            `${this.$store.state.pailUrl}/${this.$store.state.pailVersion}/cids/${this.$store.state.currentClient.cid}/catalog/metadata` + queryParams,
                            { headers: { Authorization: `Bearer ${this.accessToken}` } }
                        ).then(resp => {
                            if (resp.data) {
                                const databases = resp.data.databases || [];
                                this.dbInfo = databases.find(d => d.database === db) || {};
                                const tables = this.dbInfo.tables || [];
                                this.tableOptions = tables.map(t => ({ label: t.name, value: t.name }));
                            }
                        })
                    }
                }
            } catch (err) { this.toastError(err); }
            finally {
                this.messagesLoading = false;
                await nextTick();
                this.scrollThread();
            }
        },

        // --- Submit handlers --------------------------------------------------------------
        async handleQuestionSubmit(e) {
            if (e) e.preventDefault();
            if (!this.question.trim() || !this.database || this.streamingLoading) return;
            await this._runGenerateQuery({ db: this.database, tbl: this.table, q: this.question.trim(), isNewChat: this.streamingMessages.length == 0 });
        },
        async handleContinueSubmit(e) {
            if (e) e.preventDefault();
            if (!this.question.trim() || this.streamingLoading) return;
            await this._runGenerateQuery({
                db: this.currentDatabase,
                tbl: this.currentTable,
                q: this.question.trim(),
                isNewChat: false,
                existingResourceId: this.currentChatId,
            });
        },

        // --- Run Query button handler (from generate_query athena_query) ------------------
        async triggerExecuteQuery(generateMsg, msgIdx, mode) {
            if (this.streamingLoading) return;

            const resourceId = mode === 'new' ? this.activeStreamingChatId : this.currentChatId;
            const db = mode === 'new' ? this.database : this.currentDatabase;
            const tbl = mode === 'new' ? this.table : this.currentTable;
            const generatedQuery = generateMsg.content?.athena_query;

            const messageList = mode === 'new' ? this.streamingMessages : this.currentMessages;
            let originalQuestion = '';
            for (let i = msgIdx - 1; i >= 0; i--) {
                if (messageList[i]?.role === 'user' && messageList[i]?.content?.question) {
                    originalQuestion = messageList[i].content.question;
                    break;
                }
            }

            await this._runExecuteQuery({
                resourceId, generatedQuery, db, tbl,
                question: originalQuestion,
                mode,
            });
        },

        // --- Run Query button handler (from execute_query adjusted_query) ------------------
        async triggerExecuteQueryFromAdjusted(executeMsg, msgIdx, mode) {
            if (this.streamingLoading) return;

            const resourceId = mode === 'new' ? this.activeStreamingChatId : this.currentChatId;
            const db = mode === 'new' ? this.database : this.currentDatabase;
            const tbl = mode === 'new' ? this.table : this.currentTable;
            const generatedQuery = executeMsg.content?.adjusted_query;

            const messageList = mode === 'new' ? this.streamingMessages : this.currentMessages;
            let originalQuestion = '';
            for (let i = msgIdx - 1; i >= 0; i--) {
                if (messageList[i]?.role === 'user' && messageList[i]?.content?.question) {
                    originalQuestion = messageList[i].content.question;
                    break;
                }
            }

            await this._runExecuteQuery({
                resourceId, generatedQuery, db, tbl,
                question: originalQuestion,
                mode,
            });
        },

        // --- Generate-query SSE stream ----------------------------------------------------
        async _runGenerateQuery({ db, tbl, q, isNewChat, existingResourceId }) {
            this.streamingLoading = true;
            this.guardrailBlocked = false;
            this.guardrailMessage = "";
            this.agentStatus = null;
            this.agentStatusList = [];
            this.agentStatusExpanded = false;
            this.question = '';
            this.abortController = new AbortController();

            const resourceId = isNewChat ? `chat_${uuidv4()}` : existingResourceId ? existingResourceId : this.activeStreamingChatId;

            if (isNewChat) {
                this.activeStreamingChatId = resourceId;
                this.streamingMessages = [];
                this.newStatusExpanded = {};
            }

            const user_message = {
                role: "user",
                content: { question: q, db_name: db, table_name: tbl },
                timestamp: new Date().toISOString(),
            };

            if (isNewChat) {
                this.streamingMessages.push(user_message);
            } else {
                if (this.viewMode == 'new') {
                    this.streamingMessages = [...(this.streamingMessages || []), user_message];
                } else {
                    this.currentMessages = [...(this.currentMessages || []), user_message];
                }
            }

            if (isNewChat) {
                await this.createChat({
                    feature: "talk_to_my_data",
                    create_chat_payload: { resourceId, cid: this.currentClient.cid },
                }).catch(err => this.toastError(err));
            }

            let user_message_response = await this.upsertChatMessage({
                feature: "talk_to_my_data", resourceId,
                message_payload: { messages: [user_message] },
            }).catch(err => this.toastError(err));

            if (user_message_response?.data?.length > 0) {
                if (isNewChat || this.viewMode === 'new') {
                    this.streamingMessages[this.streamingMessages.length - 1] = user_message_response.data[0];
                } else {
                    this.currentMessages[this.currentMessages.length - 1] = user_message_response.data[0];
                }
            }

            await nextTick(); this.scrollThread();
            let assistantMsgIdx = -1

            try {
                const endpoint = mergeAgentsQueryIntoUrl(
                    `${this.$store.state.agentsUrl}/v1/cids/${this.currentClient.cid}/talk_to_my_data/generate_query`, this.$store.state
                );
                const allMessages = (isNewChat || this.viewMode === 'new')
                    ? [...this.streamingMessages]
                    : [...this.currentMessages]

                // build metadata
                let metadata = JSON.parse(JSON.stringify(this.dbInfo));
                if (tbl) {
                    metadata.tables = this.dbInfo.tables?.filter(t => t.name === tbl) || [];
                }

                const response = await fetch(endpoint, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${this.accessToken}`, 'Content-Type': 'application/json' },
                    signal: this.abortController.signal,
                    body: JSON.stringify({ question: q, db_name: db, table_name: tbl, access_token: this.accessToken, messages: allMessages, resource_id: resourceId, metadata: metadata}),
                });

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';
                let queryResponse = null;
                let keepReading = true;

                while (keepReading) {
                    const { value, done } = await reader.read();
                    if (done) break;
                    buffer += decoder.decode(value, { stream: true });
                    const lines = buffer.split('\n\n');
                    for (let i = 0; i < lines.length - 1 && !this.guardrailBlocked; i++) {
                        const line = lines[i].trim();
                        const eventMatch = line.match(/^event: (.+)$/m);
                        const dataMatch = line.match(/^data: (.+)$/m);
                        if (!eventMatch || !dataMatch) continue;
                        const eventType = eventMatch[1], dataStr = dataMatch[1];

                        if (eventType === 'message') {
                            try {
                                const data = JSON.parse(this.untruncateJson(dataStr));
                                if (data?.text_response) {
                                    queryResponse = data;
                                    const assistantMsg = {
                                        role: "assistant",
                                        msgType: "generate_query",
                                        content: { text_response: data.text_response, athena_query: data.athena_query },
                                        statusUpdates: [...this.agentStatusList],
                                        timestamp: new Date().toISOString(),
                                    };
                                    if (isNewChat || this.viewMode === 'new') {
                                        if (assistantMsgIdx >= 0) {
                                            this.streamingMessages[assistantMsgIdx] = assistantMsg;
                                        } else {
                                            this.streamingMessages = [...this.streamingMessages, assistantMsg];
                                            assistantMsgIdx = this.streamingMessages.length - 1;
                                        }
                                    } else {
                                        if (assistantMsgIdx >= 0) {
                                            this.currentMessages[assistantMsgIdx] = assistantMsg;
                                        } else {
                                            this.currentMessages = [...this.currentMessages, assistantMsg];
                                            assistantMsgIdx = this.currentMessages.length - 1;
                                        }
                                    }
                                    await nextTick(); this.scrollThread();
                                }
                            } catch (e) { console.error('message chunk', e); }
                        } else if (eventType === 'blocked') {
                            try {
                                const data = JSON.parse(dataStr);
                                this.guardrailMessage = data.reason || 'Sorry your input has been blocked by our guardrails.';
                                this.toastError({ message: this.guardrailMessage });
                                const gMsg = {
                                    role: "assistant",
                                    content: { guardrail_action: "guardrail_intervened", guardrail_message: this.guardrailMessage },
                                    timestamp: new Date().toISOString(),
                                    statusUpdates: [...this.agentStatusList]
                                };

                                if (isNewChat || this.viewMode === 'new') {
                                    if (assistantMsgIdx >= 0) this.streamingMessages[assistantMsgIdx] = gMsg;
                                    else this.streamingMessages = [...this.streamingMessages, gMsg];
                                } else {
                                    if (assistantMsgIdx >= 0) this.currentMessages[assistantMsgIdx] = gMsg;
                                    else this.currentMessages = [...this.currentMessages, gMsg];
                                }
                                await this.upsertChatMessage({ feature: "talk_to_my_data", resourceId, message_payload: { messages: [gMsg] } });
                            } catch (e) { console.error('blocked chunk', e); }
                            finally { this.guardrailBlocked = true; keepReading = false; }
                        } else if (eventType === 'status') {
                            try {
                                const p = JSON.parse(dataStr);
                                this.agentStatus = p.label;
                                this.agentStatusList.push({ label: p.label, detail: p.detail || '', timestamp: p.timestamp || new Date().toISOString().replace('T', ' ').replace('Z', '') });

                                if (assistantMsgIdx >= 0) {
                                    if (isNewChat || this.viewMode == 'new') {
                                        this.streamingMessages[assistantMsgIdx].statusUpdates = [...this.agentStatusList];
                                    } else {
                                        this.currentMessages[assistantMsgIdx].statusUpdates = [...this.agentStatusList];
                                    }
                                }
                            } catch (e) { console.error('status chunk', e); }
                        }
                    }
                    buffer = lines[lines.length - 1];
                }

                if (isNewChat) {
                    await this.updateChatTitle({ feature: "talk_to_my_data", resourceId, message_payload: { messages: [user_message] } }).catch(() => { });
                }

                if (!this.guardrailBlocked && queryResponse) {
                    const agent_message = {
                        role: "assistant",
                        msgType: "generate_query",
                        content: { text_response: queryResponse.text_response, athena_query: queryResponse.athena_query },
                        statusUpdates: [...this.agentStatusList],
                        timestamp: new Date().toISOString(),
                    };
                    await this.upsertChatMessage({ feature: "talk_to_my_data", resourceId, message_payload: { messages: [agent_message] } }).catch(err => this.toastError(err));
                }

                if (isNewChat) {
                    this.fetchChatHistory(true).then(() => { this.currentChatId = resourceId; });
                }

            } catch (err) {
                if (err.name === 'AbortError') {
                    let cancelledMsg;
                    const isNew = isNewChat || this.viewMode === 'new';
                    const msgList = isNew ? this.streamingMessages : this.currentMessages;

                    if (assistantMsgIdx >= 0) {
                        cancelledMsg = msgList[assistantMsgIdx];
                        cancelledMsg.isResponseCancelled = true;
                        if (!cancelledMsg.content?.text_response && !cancelledMsg.content?.athena_query) {
                            cancelledMsg.content = '';
                        }
                    } else {
                        cancelledMsg = {
                            role: "assistant",
                            msgType: "generate_query",
                            content: "",
                            isResponseCancelled: true,
                            statusUpdates: [...this.agentStatusList],
                            timestamp: new Date().toISOString()
                        };
                        if (isNew) {
                            this.streamingMessages = [...this.streamingMessages, cancelledMsg];
                        } else {
                            this.currentMessages = [...this.currentMessages, cancelledMsg];
                        }
                    }

                    await this.upsertChatMessage({
                        feature: "talk_to_my_data", resourceId,
                        message_payload: { messages: [cancelledMsg] },
                    }).catch(() => { });
                } else {
                    console.error('Streaming error:', err);
                }
            } finally {
                this.streamingLoading = false;
                this.agentStatus = null;
                this.abortController = null;
                this.aborting = false;
                await nextTick(); this.scrollThread();
            }
        },

        // --- Execute-query SSE stream -----------------------------------------------------
        async _runExecuteQuery({ resourceId, generatedQuery, db, tbl, question, mode }) {
            this.streamingLoading = true;
            this.agentStatus = null;
            this.agentStatusList = [];
            this.agentStatusExpanded = false;
            this.guardrailBlocked = false;
            this.abortController = new AbortController();

            const user_message = {
                role: "user",
                content: "Execute the query and give results. In case of error, adjust the query.",
                timestamp: new Date().toISOString(),
            };

            if (mode === 'new') {
                this.streamingMessages = [...this.streamingMessages, user_message];
            } else {
                this.currentMessages = [...this.currentMessages, user_message];
            }

            let user_message_response = await this.upsertChatMessage({
                feature: "talk_to_my_data", resourceId,
                message_payload: { messages: [user_message] },
            }).catch(err => this.toastError(err));

            if (user_message_response?.data?.length > 0) {
                if (mode === 'new') {
                    this.streamingMessages[this.streamingMessages.length - 1] = user_message_response.data[0];
                } else {
                    this.currentMessages[this.currentMessages.length - 1] = user_message_response.data[0];
                }
            }

            await nextTick(); this.scrollThread();
            let assistantMsgIdx = -1

            try {
                const endpoint = mergeAgentsQueryIntoUrl(
                    `${this.$store.state.agentsUrl}/v1/cids/${this.$store.state.currentClient.cid}/talk_to_my_data/execute_query`, this.$store.state
                );

                const allMessages = this.viewMode === 'new'
                    ? [...this.streamingMessages]
                    : [...this.currentMessages]

                // build metadata
                let metadata = JSON.parse(JSON.stringify(this.dbInfo));
                if (tbl) {
                    metadata.tables = this.dbInfo.tables?.filter(t => t.name === tbl) || [];
                }

                const response = await fetch(endpoint, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${this.accessToken}`, 'Content-Type': 'application/json' },
                    signal: this.abortController.signal,
                    body: JSON.stringify({
                        question,
                        generated_query: generatedQuery,
                        db_name: db,
                        table_name: tbl,
                        access_token: this.accessToken,
                        messages: [...allMessages],
                        resource_id: resourceId,
                        metadata: metadata
                    }),
                });

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';
                let forever = true;
                let execResult = null;

                while (forever) {
                    const { value, done } = await reader.read();
                    if (done) break;
                    buffer += decoder.decode(value, { stream: true });
                    const lines = buffer.split('\n\n');
                    for (let i = 0; i < lines.length - 1 && !this.guardrailBlocked; i++) {
                        const line = lines[i].trim();
                        const eventMatch = line.match(/^event: (.+)$/m);
                        const dataMatch = line.match(/^data: (.+)$/m);
                        if (!eventMatch || !dataMatch) continue;
                        const eventType = eventMatch[1], dataStr = dataMatch[1];

                        if (eventType === 'message') {
                            try {
                                const data = JSON.parse(this.untruncateJson(dataStr));
                                if (data?.final_answer || data?.query_result) {
                                    execResult = data;
                                    const assistantMsg = {
                                        role: "assistant",
                                        msgType: "execute_query",
                                        content: {
                                            query_result: data.query_result,
                                            adjusted_query: data.adjusted_query,
                                            final_answer: data.final_answer,
                                        },
                                        statusUpdates: [...this.agentStatusList],
                                        timestamp: new Date().toISOString(),
                                    };
                                    if (mode === 'new') {
                                        if (assistantMsgIdx >= 0) {
                                            this.streamingMessages[assistantMsgIdx] = assistantMsg;
                                        } else {
                                            this.streamingMessages = [...this.streamingMessages, assistantMsg];
                                            assistantMsgIdx = this.streamingMessages.length - 1;
                                        }
                                    } else {
                                        if (assistantMsgIdx >= 0) {
                                            this.currentMessages[assistantMsgIdx] = assistantMsg;
                                        } else {
                                            this.currentMessages = [...this.currentMessages, assistantMsg];
                                            assistantMsgIdx = this.currentMessages.length - 1;
                                        }
                                    }
                                    await nextTick(); this.scrollThread();
                                }
                            } catch (e) { console.error('exec message chunk', e); }
                        } else if (eventType === 'blocked') {
                            try {
                                const data = JSON.parse(dataStr);
                                const msg = data.reason || 'Input blocked by guardrails.';
                                this.toastError({ message: msg });
                                const gMsg = {
                                    role: "assistant",
                                    content: { guardrail_action: "guardrail_intervened", guardrail_message: msg },
                                    timestamp: new Date().toISOString(),
                                    statusUpdates: [...this.agentStatusList]
                                };
                                if (mode === 'new') {
                                    if (assistantMsgIdx >= 0) this.streamingMessages[assistantMsgIdx] = gMsg;
                                    else this.streamingMessages = [...this.streamingMessages, gMsg];
                                } else {
                                    if (assistantMsgIdx >= 0) this.currentMessages[assistantMsgIdx] = gMsg;
                                    else this.currentMessages = [...this.currentMessages, gMsg];
                                }
                                await this.upsertChatMessage({ feature: "talk_to_my_data", resourceId, message_payload: { messages: [gMsg] } });
                            } catch (e) { console.error('exec blocked chunk', e); }
                            finally { this.guardrailBlocked = true; forever = false; }
                        } else if (eventType === 'status') {
                            try {
                                const p = JSON.parse(dataStr);
                                this.agentStatus = p.label;
                                this.agentStatusList.push({ label: p.label, detail: p.detail || '', timestamp: p.timestamp || new Date().toISOString().replace('T', ' ').replace('Z', '') });

                                if (assistantMsgIdx >= 0) {
                                    if (mode === 'new') {
                                        this.streamingMessages[assistantMsgIdx].statusUpdates = [...this.agentStatusList];
                                    } else {
                                        this.currentMessages[assistantMsgIdx].statusUpdates = [...this.agentStatusList];
                                    }
                                }
                            } catch (e) { console.error('exec status chunk', e); }
                        }
                    }
                    buffer = lines[lines.length - 1];
                }

                if (!this.guardrailBlocked && execResult) {
                    const agent_message = {
                        role: "assistant",
                        msgType: "execute_query",
                        content: {
                            query_result: execResult.query_result,
                            adjusted_query: execResult.adjusted_query,
                            final_answer: execResult.final_answer,
                        },
                        statusUpdates: [...this.agentStatusList],
                        timestamp: new Date().toISOString(),
                    };
                    await this.upsertChatMessage({ feature: "talk_to_my_data", resourceId, message_payload: { messages: [agent_message] } }).catch(err => this.toastError(err));
                }

            } catch (err) {
                if (err.name === 'AbortError') {
                    let cancelledMsg;
                    const msgList = mode === 'new' ? this.streamingMessages : this.currentMessages;

                    if (assistantMsgIdx >= 0) {
                        cancelledMsg = msgList[assistantMsgIdx];
                        cancelledMsg.isResponseCancelled = true;
                        if (!cancelledMsg.content?.final_answer && !cancelledMsg.content?.query_result) {
                            cancelledMsg.content = '';
                        }
                    } else {
                        cancelledMsg = {
                            role: "assistant",
                            msgType: "execute_query",
                            content: "",
                            isResponseCancelled: true,
                            statusUpdates: [...this.agentStatusList],
                            timestamp: new Date().toISOString()
                        };
                        if (mode === 'new') {
                            this.streamingMessages = [...this.streamingMessages, cancelledMsg];
                        } else {
                            this.currentMessages = [...this.currentMessages, cancelledMsg];
                        }
                    }

                    await this.upsertChatMessage({
                        feature: "talk_to_my_data", resourceId,
                        message_payload: { messages: [cancelledMsg] },
                    }).catch(() => { });
                } else {
                    console.error('Execute query error:', err);
                }
            } finally {
                this.streamingLoading = false;
                this.agentStatus = null;
                this.abortController = null;
                this.aborting = false;
                await nextTick(); this.scrollThread();
            }
        },

        // --- Refine-query SSE stream -------------------------------------------------------
        async _runRefineQuery({ resourceId, query, question, customInstructions, db, tbl, mode }) {
            this.streamingLoading = true;
            this.agentStatus = null;
            this.agentStatusList = [];
            this.agentStatusExpanded = false;
            this.guardrailBlocked = false;
            this.abortController = new AbortController();

            const user_message = {
                role: "user",
                content: customInstructions
                    ? `Refine the query. Custom instructions: ${customInstructions}`
                    : "Refine the query for correctness, performance and readability.",
                timestamp: new Date().toISOString(),
            };

            if (mode === 'new') {
                this.streamingMessages = [...this.streamingMessages, user_message];
            } else {
                this.currentMessages = [...this.currentMessages, user_message];
            }

            // build metadata
            let metadata = JSON.parse(JSON.stringify(this.dbInfo));
            if (tbl) {
                metadata.tables = this.dbInfo.tables?.filter(t => t.name === tbl) || [];
            }

            let user_message_response = await this.upsertChatMessage({
                feature: "talk_to_my_data", resourceId,
                message_payload: { messages: [user_message] },
            }).catch(err => this.toastError(err));

            if (user_message_response?.data?.length > 0) {
                if (mode === 'new') {
                    this.streamingMessages[this.streamingMessages.length - 1] = user_message_response.data[0];
                } else {
                    this.currentMessages[this.currentMessages.length - 1] = user_message_response.data[0];
                }
            }

            await nextTick(); this.scrollThread();
            let assistantMsgIdx = -1

            try {
                const endpoint = mergeAgentsQueryIntoUrl(
                    `${this.$store.state.agentsUrl}/v1/cids/${this.$store.state.currentClient.cid}/talk_to_my_data/refine_query`, this.$store.state
                );

                const allMessages = this.viewMode === 'new'
                    ? [...this.streamingMessages]
                    : [...this.currentMessages]

                const response = await fetch(endpoint, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${this.accessToken}`, 'Content-Type': 'application/json' },
                    signal: this.abortController.signal,
                    body: JSON.stringify({
                        question, query, db_name: db, table_name: tbl,
                        custom_instructions: customInstructions || null,
                        access_token: this.accessToken,
                        messages: [...allMessages],
                        resource_id: resourceId,
                        metadata: metadata
                    }),
                });

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';
                let forever = true;
                let refineResult = null;

                while (forever) {
                    const { value, done } = await reader.read();
                    if (done) break;
                    buffer += decoder.decode(value, { stream: true });
                    const lines = buffer.split('\n\n');
                    for (let i = 0; i < lines.length - 1 && !this.guardrailBlocked; i++) {
                        const line = lines[i].trim();
                        const eventMatch = line.match(/^event: (.+)$/m);
                        const dataMatch = line.match(/^data: (.+)$/m);
                        if (!eventMatch || !dataMatch) continue;
                        const eventType = eventMatch[1], dataStr = dataMatch[1];

                        if (eventType === 'message') {
                            try {
                                const data = JSON.parse(this.untruncateJson(dataStr));
                                if (data?.text_response || data?.refined_query) {
                                    refineResult = data;
                                    const assistantMsg = {
                                        role: "assistant",
                                        msgType: "refine_query",
                                        content: {
                                            text_response: data.text_response,
                                            refined_query: data.refined_query,
                                        },
                                        statusUpdates: [...this.agentStatusList],
                                        timestamp: new Date().toISOString(),
                                    };
                                    if (mode === 'new') {
                                        if (assistantMsgIdx >= 0) {
                                            this.streamingMessages[assistantMsgIdx] = assistantMsg;
                                        } else {
                                            this.streamingMessages = [...this.streamingMessages, assistantMsg];
                                            assistantMsgIdx = this.streamingMessages.length - 1;
                                        }
                                    } else {
                                        if (assistantMsgIdx >= 0) {
                                            this.currentMessages[assistantMsgIdx] = assistantMsg;
                                        } else {
                                            this.currentMessages = [...this.currentMessages, assistantMsg];
                                            assistantMsgIdx = this.currentMessages.length - 1;
                                        }
                                    }
                                    await nextTick(); this.scrollThread();
                                }
                            } catch (e) { console.error('refine message chunk', e); }
                        } else if (eventType === 'blocked') {
                            try {
                                const data = JSON.parse(dataStr);
                                const msg = data.reason || 'Input blocked by guardrails.';
                                this.toastError({ message: msg });
                                const gMsg = {
                                    role: "assistant",
                                    content: { guardrail_action: "guardrail_intervened", guardrail_message: msg },
                                    timestamp: new Date().toISOString(),
                                    statusUpdates: [...this.agentStatusList]
                                };
                                if (mode === 'new') {
                                    if (assistantMsgIdx >= 0) this.streamingMessages[assistantMsgIdx] = gMsg;
                                    else this.streamingMessages = [...this.streamingMessages, gMsg];
                                } else {
                                    if (assistantMsgIdx >= 0) this.currentMessages[assistantMsgIdx] = gMsg;
                                    else this.currentMessages = [...this.currentMessages, gMsg];
                                }
                                await this.upsertChatMessage({ feature: "talk_to_my_data", resourceId, message_payload: { messages: [gMsg] } });
                            } catch (e) { console.error('refine blocked chunk', e); }
                            finally { this.guardrailBlocked = true; forever = false; }
                        } else if (eventType === 'status') {
                            try {
                                const p = JSON.parse(dataStr);
                                this.agentStatus = p.label;
                                this.agentStatusList.push({ label: p.label, detail: p.detail || '', timestamp: p.timestamp || new Date().toISOString().replace('T', ' ').replace('Z', '') });
                                if (assistantMsgIdx >= 0) {
                                    if (mode === 'new') this.streamingMessages[assistantMsgIdx].statusUpdates = [...this.agentStatusList];
                                    else this.currentMessages[assistantMsgIdx].statusUpdates = [...this.agentStatusList];
                                }
                            } catch (e) { console.error('refine status chunk', e); }
                        }
                    }
                    buffer = lines[lines.length - 1];
                }

                if (!this.guardrailBlocked && refineResult) {
                    const agent_message = {
                        role: "assistant",
                        msgType: "refine_query",
                        content: {
                            text_response: refineResult.text_response,
                            refined_query: refineResult.refined_query,
                        },
                        statusUpdates: [...this.agentStatusList],
                        timestamp: new Date().toISOString(),
                    };
                    await this.upsertChatMessage({ feature: "talk_to_my_data", resourceId, message_payload: { messages: [agent_message] } }).catch(err => this.toastError(err));
                }

            } catch (err) {
                if (err.name === 'AbortError') {
                    let cancelledMsg;
                    const msgList = mode === 'new' ? this.streamingMessages : this.currentMessages;

                    if (assistantMsgIdx >= 0) {
                        cancelledMsg = msgList[assistantMsgIdx];
                        cancelledMsg.isResponseCancelled = true;
                        if (!cancelledMsg.content?.text_response && !cancelledMsg.content?.refined_query) {
                            cancelledMsg.content = '';
                        }
                    } else {
                        cancelledMsg = {
                            role: "assistant",
                            msgType: "refine_query",
                            content: "",
                            isResponseCancelled: true,
                            statusUpdates: [...this.agentStatusList],
                            timestamp: new Date().toISOString()
                        };
                        if (mode === 'new') {
                            this.streamingMessages = [...this.streamingMessages, cancelledMsg];
                        } else {
                            this.currentMessages = [...this.currentMessages, cancelledMsg];
                        }
                    }

                    await this.upsertChatMessage({
                        feature: "talk_to_my_data", resourceId,
                        message_payload: { messages: [cancelledMsg] },
                    }).catch(() => { });
                } else {
                    console.error('Refine query error:', err);
                }
            } finally {
                this.streamingLoading = false;
                this.agentStatus = null;
                this.abortController = null;
                this.aborting = false;
                await nextTick(); this.scrollThread();
            }
        },

        // --- Run Query from refine result --------------------------------------------------
        async triggerExecuteQueryFromRefined(refineMsg, msgIdx, mode) {
            if (this.streamingLoading) return;
            const resourceId = mode === 'new' ? this.activeStreamingChatId : this.currentChatId;
            const db = mode === 'new' ? this.database : this.currentDatabase;
            const tbl = mode === 'new' ? this.table : this.currentTable;
            const generatedQuery = refineMsg.content?.refined_query;
            const messageList = mode === 'new' ? this.streamingMessages : this.currentMessages;
            let originalQuestion = '';
            for (let i = msgIdx - 1; i >= 0; i--) {
                if (messageList[i]?.role === 'user' && messageList[i]?.content?.question) {
                    originalQuestion = messageList[i].content.question;
                    break;
                }
            }
            await this._runExecuteQuery({
                resourceId, generatedQuery, db, tbl,
                question: originalQuestion,
                mode,
            });
        },

        // --- History -----------------------------------------------------------------------
        async fetchChatHistory(forceGet = false) {
            this.historyLoading = true;
            await this.getChats({ feature: "talk_to_my_data", limit: this.chatLimit, forceGet }).catch(err => this.toastError(err));
            this.historyLoading = false;
            this.checkCurrentChatPresence();
        },
        async fetchAdminHistory(forceGet = false) {
            this.adminLoading = true;
            await this.getChats({ feature: "talk_to_my_data", limit: this.adminChatLimitInput, adminHistory: true, forceGet }).catch(err => this.toastError(err));
            this.adminLoading = false;
            this.checkCurrentChatPresence();
        },
        async switchToAdmin() {
            this.activeTab = 'admin';
            if (!this.adminChats || this.adminChats.length === 0) await this.fetchAdminHistory();
        },

        // --- Delete ------------------------------------------------------------------------------------
        confirmDeleteChat(chats) {
            this.showModal({
                component: "deleteChatHistory",
                data: {
                    feature: "talk_to_my_data",
                    chats,
                    refreshChatsFunction: () => {
                        this.fetchChatHistory(true);

                        const isCurrentChatDeleted = chats.some(c => c.resourceId === this.currentChatId);
                        if (isCurrentChatDeleted) {
                            this.startNewChat();
                        }
                    },
                    screensize: 'xl'
                }
            });
        },

        checkCurrentChatPresence() {
            if (this.viewMode === 'history' && this.currentChatId) {
                const currentList = this.activeTab === 'admin' ? this.adminChats : this.chats;
                const isPresent = currentList?.some(chat => chat.resourceId === this.currentChatId);
                if (!isPresent) {
                    this.startNewChat();
                }
            }
        },

        // --- DB / Table --------------------------------------------------------------------------------
        async getDatabases() {
            try {
                const resp = await axios.get(`${this.$store.state.pailUrl}/${this.$store.state.pailVersion}/cids/${this.$store.state.currentClient.cid}/dbs`, { headers: { Authorization: `Bearer ${this.accessToken}` } });
                if (Array.isArray(resp.data)) this.databaseOptions = resp.data.map(db => ({ label: db, value: db }));
            } catch (e) { console.error('getDatabases', e); }
        },
        async handleDatabaseChange(newValue) {
            this.table = ""; this.tableOptions = []; this.tablesLoading = true;
            if (!newValue) return;
            try {
                const resp = await axios.get(`${this.$store.state.pailUrl}/${this.$store.state.pailVersion}/cids/${this.$store.state.currentClient.cid}/catalog/metadata?database=${newValue}`, { headers: { Authorization: `Bearer ${this.accessToken}` } });
                if (resp.data) {
                    const databases = resp.data.databases || [];
                    this.dbInfo = databases.find(d => d.database === newValue) || {};
                    const tables = this.dbInfo.tables || [];
                    this.tableOptions = tables.map(t => ({ label: t.name, value: t.name }));
                }
            } catch (e) { 
                console.error('handleDatabaseChange', e);

                // TODO: remove this once /metadata api is available in production
                const statusCode = e?.response?.status;
                const isMetadataApiFailed = typeof statusCode === "number" && statusCode >= 400 && statusCode < 600;

                if (isMetadataApiFailed) {
                    await axios.get(`${this.$store.state.pailUrl}/${this.$store.state.pailVersion}/cids/${this.$store.state.currentClient.cid}/dbs/${newValue}/tables`, { headers: { Authorization: `Bearer ${this.accessToken}` } })
                        .then(resp => {
                            if (Array.isArray(resp.data?.tables)) {
                                this.tableOptions = resp.data.tables.map(t => ({ label: t, value: t }));
                            }
                        })
                        .catch(err => console.error('getTables fallback', err));
                }
            }
            finally { this.tablesLoading = false; }
        },

        // --- Save query modal -------------------------------------------------------------------------
        saveQuery(query) {
            this.showModal({ component: "AddQuery", data: { query: { Name: "", Description: "", QueryString: query, Internal: false, Params: [] } } });
        },

        // --- Scroll --------------------------------------------------------------------------------------------
        scrollThread() {
            const ref = this.viewMode === 'history' ? this.$refs.historyThread : this.$refs.messageThread;
            if (ref) ref.scrollTo({ top: ref.scrollHeight, behavior: 'smooth' });
        },

        // --- Toast ---------------------------------------------------------------------------------------------
        toastError(err) {
            const msg = err?.response?.data?.error || err?.message || 'An error occurred';
            this.$store.dispatch('addToast', { type: 'error', message: msg });
        },

        // --- Refine panel open/close --------------------------------------------------------------------------
        toggleRefinePanel(key) {
            this.refineOpen = {
                ...this.refineOpen,
                [key]: !this.refineOpen[key],
            };
            if (!this.refineText[key]) {
                this.refineText = { ...this.refineText, [key]: '' };
            }
        },

        closeRefinePanel(key) {
            this.refineOpen = { ...this.refineOpen, [key]: false };
        },

        submitRefine(key, query, msgIdx, mode) {
            const customInstructions = (this.refineText[key] || '').trim() || null;
            this.closeRefinePanel(key);

            const messageList = mode === 'new' ? this.streamingMessages : this.currentMessages;
            let originalQuestion = '';
            for (let i = msgIdx - 1; i >= 0; i--) {
                if (messageList[i]?.role === 'user' && messageList[i]?.content?.question) {
                    originalQuestion = messageList[i].content.question;
                    break;
                }
            }

            const resourceId = mode === 'new' ? this.activeStreamingChatId : this.currentChatId;
            const db = mode === 'new' ? this.database : this.currentDatabase;
            const tbl = mode === 'new' ? this.table : this.currentTable;

            this._runRefineQuery({
                resourceId, query,
                question: originalQuestion,
                customInstructions, db, tbl,
                mode,
            });
        },

        handleRefineKeydown(e, key, query, msgIdx, mode) {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                this.submitRefine(key, query, msgIdx, mode);
            }
            if (e.key === 'Escape') {
                this.closeRefinePanel(key);
            }
        },
    },

    watch: {
        streamingLoading(streaming) {
            if (streaming) registerAgentStream(this, 'Talk to My Data', () => this.abortStream());
            else releaseAgentStream(this);
        },
        chatLimit(v) { this.$store.dispatch("amdp/setT2MDChatLimit", v); },
        adminChatLimitInput(v) { this.$store.dispatch("amdp/setT2MDAdminChatLimit", v); },
    },

    async mounted() {
        this.chatLimit = this.storedChatLimit || 50;
        this.adminChatLimitInput = this.storedAdminChatLimit || 100;
        this.fetchChatHistory();
        await this.getDatabases();
    },
};
</script>

<style scoped>
/* ═══════════════════════════════════════════════════
     DESIGN TOKENS - DARK
  ═══════════════════════════════════════════════════ */
.t2md-shell.dark {
    --color-bg: #0f1117;
    --color-surface: #181c26;
    --color-surface-2: #1e2333;
    --color-surface-3: #252c40;
    --color-border: rgba(255, 255, 255, 0.07);
    --color-border-hover: rgba(255, 255, 255, 0.15);
    --color-primary: #5b8cff;
    --color-primary-dim: rgba(91, 140, 255, 0.15);
    --color-accent: #a78bfa;
    --color-danger: #f87171;
    --color-text: #e4e8f0;
    --color-text-muted: #6b7a99;
    --color-text-dim: #3d4766;
    --color-success: #4ade80;
    --qd-pre-bg: #111520;
    --qd-pre-text: #a8b4d0;
    --qd-pre-border: rgba(91, 140, 255, 0.2);
    --qd-table-bg: #181c26;
    --qd-thead-bg: #111317;
    --qd-thead-text: #a0a4b8;
    --qd-td-border: rgba(255, 255, 255, 0.06);
    --qd-tr-hover: rgba(91, 140, 255, 0.05);
    --qd-label-color: #6b7a99;
    --qd-blocked-bg: rgba(248, 113, 113, 0.08);
    --qd-blocked-text: #f87171;
    --qd-blocked-border: rgba(248, 113, 113, 0.3);
    --msg-user-bg: #1e2d4a;
    --msg-user-border: rgba(91, 140, 255, 0.2);
    --status-border: rgba(255, 255, 255, 0.06);
    --status-text: #a0a4b8;
    --executed-bg: rgba(74, 222, 128, 0.1);
    --executed-text: #4ade80;
    --executed-border: rgba(74, 222, 128, 0.25);
}

/* ═══════════════════════════════════════════════════
     DESIGN TOKENS - LIGHT
  ═══════════════════════════════════════════════════ */
.t2md-shell.light {
    --color-bg: #f4f6fb;
    --color-surface: #ffffff;
    --color-surface-2: #f0f3fa;
    --color-surface-3: #e4e9f5;
    --color-border: rgba(0, 0, 0, 0.08);
    --color-border-hover: rgba(0, 0, 0, 0.18);
    --color-primary: #3b6ef5;
    --color-primary-dim: rgba(59, 110, 245, 0.10);
    --color-accent: #7c3aed;
    --color-danger: #dc2626;
    --color-text: #1a1f36;
    --color-text-muted: #6b7280;
    --color-text-dim: #9ca3af;
    --color-success: #16a34a;
    --qd-pre-bg: #f0f4ff;
    --qd-pre-text: #2d3a5c;
    --qd-pre-border: rgba(59, 110, 245, 0.18);
    --qd-table-bg: #ffffff;
    --qd-thead-bg: #f0f3fa;
    --qd-thead-text: #374151;
    --qd-td-border: #e5e7eb;
    --qd-tr-hover: rgba(59, 110, 245, 0.04);
    --qd-label-color: #6b7280;
    --qd-blocked-bg: #fef2f2;
    --qd-blocked-text: #991b1b;
    --qd-blocked-border: rgba(220, 38, 38, 0.3);
    --msg-user-bg: #e7f0ff;
    --msg-user-border: rgba(59, 110, 245, 0.15);
    --status-border: #e9ecef;
    --status-text: #6b7280;
    --executed-bg: #f0fdf4;
    --executed-text: #15803d;
    --executed-border: rgba(22, 163, 74, 0.25);
}

/* ═══════════════════════════════════════════════════
     BASE SHELL
  ═══════════════════════════════════════════════════ */
.t2md-shell {
    --sidebar-width: 280px;
    --topbar-h: 56px;
    --radius-sm: 6px;
    --radius-md: 10px;
    --radius-lg: 16px;
    --font-sans: 'DM Sans', 'Segoe UI', system-ui, sans-serif;
    --font-mono: 'JetBrains Mono', 'Fira Code', monospace;
    --transition: 0.18s ease;
    display: flex;
    height: 100vh;
    overflow: hidden;
    background: var(--color-bg);
    color: var(--color-text);
}

/* ═══════════════════════════════════════════════════
     SIDEBAR
  ═══════════════════════════════════════════════════ */
.t2md-sidebar {
    width: var(--sidebar-width);
    min-width: var(--sidebar-width);
    background: var(--color-surface);
    border-right: 1px solid var(--color-border);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    transition: width var(--transition), min-width var(--transition);
    flex-shrink: 0;
}

.t2md-sidebar.collapsed {
    width: 60px;
    min-width: 60px;
}

.sidebar-header-t2md {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 14px;
    height: var(--topbar-h);
    border-bottom: 1px solid var(--color-border);
    flex-shrink: 0;
}

.sidebar-brand {
    display: flex;
    align-items: center;
    gap: 10px;
    overflow: hidden;
}

.brand-icon {
    width: 32px;
    height: 32px;
    border-radius: var(--radius-sm);
    background: var(--color-primary-dim);
    border: 1px solid var(--color-primary-dim);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--color-primary);
    font-size: 13px;
    flex-shrink: 0;
}

.brand-text {
    font-size: 20px;
    font-weight: 700;
    color: var(--color-text);
    white-space: nowrap;
}

.sidebar-toggle {
    background: none;
    border: none;
    color: var(--color-text-muted);
    cursor: pointer;
    padding: 6px;
    border-radius: var(--radius-sm);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color var(--transition), background var(--transition);
}

.sidebar-toggle:hover {
    color: var(--color-text);
    background: var(--color-surface-2);
}

.sidebar-actions {
    padding: 12px 12px 4px;
    flex-shrink: 0;
}

.btn-new-chat {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 9px 14px;
    border-radius: var(--radius-md);
    border: 1px solid var(--color-border);
    background: var(--color-surface-2);
    color: var(--color-text);
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: background var(--transition), border-color var(--transition);
}

.btn-new-chat:hover {
    background: var(--color-surface-3);
    border-color: var(--color-primary);
    color: var(--color-primary);
}

.btn-new-chat i {
    color: var(--color-primary);
}

.btn-new-chat-icon {
    width: 36px;
    height: 36px;
    border-radius: var(--radius-md);
    border: 1px solid var(--color-border);
    background: var(--color-surface-2);
    color: var(--color-primary);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    margin: 0 auto;
    transition: background var(--transition), border-color var(--transition);
}

.btn-new-chat-icon:hover {
    background: var(--color-surface-3);
    border-color: var(--color-primary);
}

.sidebar-tabs {
    display: flex;
    gap: 4px;
    padding: 8px 12px 0;
    flex-shrink: 0;
}

.tab-btn {
    flex: 1;
    padding: 5px 8px;
    border: none;
    border-bottom: 2px solid transparent;
    background: none;
    color: var(--color-text-muted);
    font-size: 11.5px;
    font-weight: 600;
    letter-spacing: 0.3px;
    text-transform: uppercase;
    cursor: pointer;
    transition: color var(--transition), border-color var(--transition);
}

.tab-btn.active {
    color: var(--color-primary);
    border-bottom-color: var(--color-primary);
}

.tab-btn:hover:not(.active) {
    color: var(--color-text);
}

.sidebar-list {
    flex: 1;
    overflow-y: auto;
    padding: 8px 0;
    scrollbar-width: thin;
    scrollbar-color: var(--color-surface-3) transparent;
}

.sidebar-list-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px 14px 8px;
}

.list-count {
    font-size: 11px;
    color: var(--color-text-muted);
    font-weight: 600;
    letter-spacing: 0.4px;
    text-transform: uppercase;
}

.list-actions {
    display: flex;
    align-items: center;
    gap: 6px;
}

.limit-input {
    width: 52px;
    padding: 3px 6px;
    border-radius: var(--radius-sm);
    border: 1px solid var(--color-border);
    background: var(--color-surface-2);
    color: var(--color-text);
    font-size: 12px;
    text-align: center;
    outline: none;
}

.limit-input:focus {
    border-color: var(--color-primary);
}

.icon-btn {
    width: 28px;
    height: 28px;
    border-radius: var(--radius-sm);
    border: 1px solid var(--color-border);
    background: var(--color-surface-2);
    color: var(--color-text-muted);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    transition: color var(--transition), border-color var(--transition);
}

.icon-btn:hover:not(:disabled) {
    color: var(--color-text);
    border-color: var(--color-border-hover);
}

.icon-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.icon-btn.danger:hover {
    color: var(--color-danger);
    border-color: var(--color-danger);
}

.sidebar-loading,
.sidebar-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 28px 16px;
    color: var(--color-text-muted);
    font-size: 13px;
}

.sidebar-loading i {
    font-size: 20px;
}

.sidebar-empty i {
    font-size: 24px;
    opacity: 0.4;
}

.sidebar-empty p {
    margin: 0;
    font-size: 12px;
}

.chat-list {
    display: flex;
    flex-direction: column;
}

.chat-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 12px;
    border-radius: var(--radius-sm);
    margin: 1px 6px;
    cursor: pointer;
    transition: background var(--transition);
    position: relative;
}

.chat-item:hover {
    background: var(--color-surface-2);
}

.chat-item.active {
    background: var(--color-primary-dim);
}

.chat-item-icon {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: var(--color-surface-3);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    color: var(--color-text-muted);
    flex-shrink: 0;
    margin-top: 1px;
}

.chat-item-body {
    flex: 1;
    min-width: 0;
}

.chat-item-title {
    font-size: 12.5px;
    font-weight: 500;
    color: var(--color-text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.4;
}

.chat-item-meta {
    font-size: 10.5px;
    color: var(--color-text-muted);
    margin-top: 2px;
    display: flex;
    align-items: center;
    gap: 5px;
}

.user-badge {
    background: rgba(167, 139, 250, 0.15);
    color: var(--color-accent);
    padding: 1px 5px;
    border-radius: 3px;
    font-size: 9.5px;
    font-weight: 600;
    max-width: 100px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.chat-item-delete {
    opacity: 0;
    background: none;
    border: none;
    color: var(--color-text-muted);
    font-size: 11px;
    cursor: pointer;
    padding: 3px;
    border-radius: 4px;
    transition: color var(--transition), opacity var(--transition);
    flex-shrink: 0;
}

.chat-item:hover .chat-item-delete {
    opacity: 1;
}

.chat-item-delete:hover {
    color: var(--color-danger);
}

/* ═══════════════════════════════════════════════════
     MAIN + TOPBAR
  ═══════════════════════════════════════════════════ */
.t2md-main {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: var(--color-bg);
}

.t2md-topbar {
    height: var(--topbar-h);
    border-bottom: 1px solid var(--color-border);
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 16px;
    flex-shrink: 0;
    background: var(--color-surface);
    gap: 12px;
}

.topbar-left {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;
    min-width: 0;
    overflow: hidden;
}

.topbar-right {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
}

.topbar-selects {
    display: flex;
    align-items: center;
    gap: 6px;
    flex: 1;
    min-width: 0;
}

.topbar-select-group {
    display: flex;
    align-items: center;
    gap: 5px;
}

.topbar-select-icon {
    font-size: 11px;
    color: var(--color-primary);
    flex-shrink: 0;
}

.topbar-select {
    padding: 4px 24px 4px 8px;
    border-radius: var(--radius-sm);
    border: 1px solid var(--color-border);
    background: var(--color-surface-2);
    color: var(--color-text);
    font-size: 12.5px;
    outline: none;
    cursor: pointer;
    appearance: none;
    max-width: 180px;
    min-width: 100px;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7a99' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 7px center;
    transition: border-color var(--transition);
}

.topbar-select:focus {
    border-color: var(--color-primary);
}

.topbar-select:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.topbar-select-divider {
    color: var(--color-text-dim);
    font-size: 14px;
    flex-shrink: 0;
}

.topbar-loading-inline {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: var(--color-text-muted);
}

.context-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 9px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.3px;
    text-transform: uppercase;
    white-space: nowrap;
    flex-shrink: 0;
}

.context-badge.history {
    background: rgba(167, 139, 250, 0.12);
    color: var(--color-accent);
    border: 1px solid rgba(167, 139, 250, 0.25);
}

.context-badge.new {
    background: var(--color-primary-dim);
    color: var(--color-primary);
    border: 1px solid var(--color-primary-dim);
}

.context-title {
    font-size: 13.5px;
    font-weight: 600;
    color: var(--color-text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.meta-chip {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 4px 10px;
    border-radius: 20px;
    background: var(--color-surface-2);
    border: 1px solid var(--color-border);
    font-size: 11.5px;
    color: var(--color-text-muted);
    white-space: nowrap;
}

.meta-chip i {
    font-size: 10px;
}

/* ═══════════════════════════════════════════════════
     CHAT WORKSPACE
  ═══════════════════════════════════════════════════ */
.chat-workspace {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.messages-container {
    flex: 1;
    overflow-y: auto;
    padding: 2rem 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 0;
    scrollbar-width: thin;
    scrollbar-color: var(--color-surface-3) transparent;
}

.messages-container::-webkit-scrollbar {
    width: 6px;
}

.messages-container::-webkit-scrollbar-thumb {
    background: var(--color-surface-3);
    border-radius: 3px;
}

/* ── Empty states ── */
.empty-state {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 40px 24px;
}

.empty-icon {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    background: var(--color-surface-2);
    border: 1px solid var(--color-border);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 28px;
    color: var(--color-primary);
    margin-bottom: 20px;
}

.empty-title {
    font-size: 22px;
    font-weight: 700;
    color: var(--color-text);
    margin: 0 0 8px;
}

.empty-subtitle {
    font-size: 14px;
    color: var(--color-text-muted);
    margin: 0 0 24px;
    max-width: 380px;
    line-height: 1.6;
}

.empty-examples {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: center;
    max-width: 520px;
}

.example-chip {
    padding: 7px 14px;
    border-radius: 20px;
    border: 1px solid var(--color-border);
    background: var(--color-surface-2);
    font-size: 12.5px;
    color: var(--color-text-muted);
    cursor: pointer;
    transition: all var(--transition);
}

.example-chip:hover {
    border-color: var(--color-primary);
    color: var(--color-primary);
    background: var(--color-primary-dim);
}

.loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 60px;
    color: var(--color-text-muted);
}

.loading-state i {
    font-size: 24px;
    color: var(--color-primary);
}

/* ═══════════════════════════════════════════════════
     MESSAGES
  ═══════════════════════════════════════════════════ */
.message {
    display: flex;
    margin-bottom: 1.5rem;
    animation: fadeIn 0.25s ease;
    width: 100%;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(8px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.message-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    flex-shrink: 0;
    margin-right: 1rem;
}

.message-avatar.user {
    background: var(--color-primary);
    color: white;
}

.message-avatar.assistant {
    background: var(--color-surface);
    border: 1px solid var(--color-border);
    color: var(--color-accent);
}

.message-content {
    flex: 1;
    min-width: 0;
}

.message-text {
    background: var(--color-surface-2);
    border: 1px solid var(--color-border);
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
    line-height: 1.6;
    word-wrap: break-word;
    font-size: 14px;
}

.message.user .message-text {
    background: var(--msg-user-bg);
    border-color: var(--msg-user-border);
}

.msg-question {
    font-weight: 500;
}

.msg-context-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 8px;
}

.tag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 8px;
    border-radius: 4px;
    background: var(--color-primary-dim);
    color: var(--color-primary);
    font-size: 11px;
    font-weight: 600;
}

.assistant-bubble {
    background: var(--color-surface);
    border: 1px solid var(--color-border);
    border-radius: 0.5rem;
    padding: 0.75rem 1rem;
    overflow-x: scroll;
}

/* ═══════════════════════════════════════════════════
     AGENT STATUS ACCORDION - PAICE Chat style
  ═══════════════════════════════════════════════════ */
.agent-status {
    display: flex;
    align-items: center;
    gap: 7px;
    cursor: pointer;
    user-select: none;
    font-size: 13px;
    padding: 2px 0;
}

.status-text {
    font-size: 13px;
    color: var(--status-text);
}

.agent-status.collapsed .collapse-arrow-up {
    display: none;
}

.agent-status:not(.collapsed) .collapse-arrow-down {
    display: none;
}

.collapse-arrow-down,
.collapse-arrow-up {
    font-size: 11px;
    color: var(--color-text-muted);
}

.status-list {
    margin-top: 8px;
    border-top: 1px solid var(--status-border);
    max-height: 280px;
    overflow-y: auto;
}

.status-row {
    display: grid;
    grid-template-columns: 20px 130px 1fr auto;
    gap: 8px;
    align-items: start;
    padding: 7px 4px;
    border-bottom: 1px solid var(--status-border);
    font-size: 12px;
}

.status-row:last-child {
    border-bottom: none;
}

.status-icon-col {
    display: flex;
    align-items: center;
    justify-content: center;
    padding-top: 3px;
    color: var(--color-text-muted);
}

.status-label-col {
    color: var(--color-text-muted);
    word-break: break-word;
}

.status-detail-col {
    color: var(--color-text-muted);
}

.status-time-col {
    color: var(--color-text-dim);
    font-size: 11px;
    white-space: nowrap;
    text-align: right;
}

/* Streaming dots */
.streaming-indicator {
    display: flex;
    gap: 0.25rem;
    margin-top: 0.5rem;
}

.streaming-indicator .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--color-text-muted);
    animation: pulse 1.5s infinite;
}

.streaming-indicator .dot:nth-child(2) {
    animation-delay: 0.2s;
}

.streaming-indicator .dot:nth-child(3) {
    animation-delay: 0.4s;
}

@keyframes pulse {

    0%,
    60%,
    100% {
        opacity: 0.3;
        transform: scale(0.8);
    }

    30% {
        opacity: 1;
        transform: scale(1);
    }
}

/* Blocked message */
.blocked-message {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    background: var(--qd-blocked-bg);
    color: var(--qd-blocked-text);
    border: 1px solid var(--qd-blocked-border);
    padding: 10px 14px;
    border-radius: var(--radius-md);
    font-size: 13.5px;
    font-style: italic;
}

/* Utilities */
.mb-2 {
    margin-bottom: 8px;
}

.fw-bold {
    font-weight: 600;
}

.fst-italic {
    font-style: italic;
}

.text-success {
    color: var(--color-success) !important;
}

.text-danger {
    color: var(--color-danger) !important;
}

/* ═══════════════════════════════════════════════════
     QUERY DISPLAY
  ═══════════════════════════════════════════════════ */
.qd-markdown {
    font-size: 14px;
    line-height: 1.7;
    color: var(--color-text);
}

.qd-markdown :deep(p) {
    margin: 0 0 8px;
}

.qd-markdown :deep(code) {
    font-family: var(--font-mono);
    font-size: 12.5px;
    background: var(--qd-pre-bg);
    padding: 1px 5px;
    border-radius: 3px;
}

.qd-markdown :deep(table) {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    margin-bottom: 8px;
}

.qd-markdown :deep(th) {
    background: var(--qd-thead-bg);
    color: var(--qd-thead-text);
    padding: 6px 10px;
    text-align: left;
    font-weight: 600;
    font-size: 12px;
}

.qd-markdown :deep(td) {
    padding: 6px 10px;
    border-bottom: 1px solid var(--qd-td-border);
    color: var(--color-text);
}

.qd-pre {
    font-family: var(--font-mono);
    font-size: 12.5px;
    background: var(--qd-pre-bg);
    color: var(--qd-pre-text);
    border: 1px solid var(--qd-pre-border);
    border-radius: var(--radius-md);
    padding: 12px 14px;
    margin: 10px 0 0;
    white-space: pre-wrap;
    word-break: break-all;
    line-height: 1.6;
    overflow-x: auto;
}

.qd-actions {
    display: flex;
    gap: 8px;
    margin-top: 12px;
    flex-wrap: wrap;
}

.qd-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    border-radius: var(--radius-md);
    font-size: 12.5px;
    font-weight: 600;
    cursor: pointer;
    border: none;
}

.qd-btn:hover:not(:disabled) {
    opacity: 0.85;
}

.qd-btn:disabled {
    opacity: 0.40;
    cursor: not-allowed;
    transform: none;
}

.qd-btn.run {
    background: #16a34a;
    color: white;
}

.qd-btn.run:disabled {
    background: #16a34a;
    color: white;
}

.qd-btn.save {
    background: var(--color-surface-3);
    color: var(--color-text-muted);
    border: 1px solid var(--color-border);
}

.qd-section-label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: var(--qd-label-color);
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 6px;
}

.qd-section-label i {
    color: var(--color-primary);
}

.qd-results {
    margin-top: 14px;
}

.qd-table-wrap {
    max-height: 360px;
    overflow-y: auto;
    border-radius: var(--radius-md);
    border: 1px solid var(--color-border);
}

.qd-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    background: var(--qd-table-bg);
}

.qd-table thead th {
    position: sticky;
    top: 0;
    background: var(--qd-thead-bg);
    color: var(--qd-thead-text);
    padding: 8px 12px;
    text-align: left;
    font-size: 11.5px;
    font-weight: 700;
    letter-spacing: 0.3px;
    border-bottom: 1px solid var(--color-border);
}

.qd-table tbody tr:hover {
    background: var(--qd-tr-hover);
}

.qd-table td {
    padding: 7px 12px;
    border-bottom: 1px solid var(--qd-td-border);
    color: var(--color-text);
    font-size: 13px;
}

.qd-table tbody tr:last-child td {
    border-bottom: none;
}

.qd-analysis {
    margin-top: 14px;
}

.qd-error {
    padding: 10px 14px;
    border-radius: var(--radius-md);
    background: var(--qd-blocked-bg);
    color: var(--qd-blocked-text);
    border: 1px solid var(--qd-blocked-border);
    font-size: 13px;
}

/* ═══════════════════════════════════════════════════
     INPUT BAR
  ═══════════════════════════════════════════════════ */
.input-bar-wrapper {
    flex-shrink: 0;
    padding: 16px 24px 12px;
    background: var(--color-surface);
    border-top: 1px solid var(--color-border);
}

.input-bar {
    display: flex;
    align-items: flex-end;
    gap: 10px;
    padding: 8px 12px;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-lg);
    background: var(--color-surface-2);
    transition: border-color var(--transition), box-shadow var(--transition);
}

.input-bar:focus-within {
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px var(--color-primary-dim);
}

.question-textarea {
    flex: 1;
    background: none;
    border: none;
    outline: none;
    resize: none;
    color: var(--color-text);
    font-size: 14px;
    font-family: var(--font-sans);
    line-height: 20px;
    height: 20px;
    max-height: 20px;
    overflow-y: auto;
    padding: 8px 0;
    margin: 0;
    box-sizing: content-box;
}

.question-textarea::placeholder {
    color: var(--color-text-muted);
}

.textarea-disabled {
    opacity: 0.5;
}

.send-btn {
    width: 36px;
    height: 36px;
    border-radius: var(--radius-md);
    border: none;
    color: white;
    font-size: 14px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: background var(--transition), transform var(--transition);
}

.send-btn:hover:not(:disabled) {
    opacity: 0.85;
    transform: scale(1.05);
}

.send-btn:disabled {
    cursor: not-allowed;
    transform: none;
}

.input-disclaimer {
    font-size: 11px;
    color: var(--color-text-dim);
    text-align: center;
    margin: 8px 0 0;
}

th {
    border-color: var(--color-surface-3) !important;
}

/* ═══════════════════════════════════════════════════
     RESPONSIVE
  ═══════════════════════════════════════════════════ */
@media (max-width: 768px) {
    .t2md-sidebar {
        position: fixed;
        z-index: 200;
        height: 100%;
        transform: translateX(0);
        transition: transform var(--transition), width var(--transition);
    }

    .t2md-sidebar.collapsed {
        transform: translateX(-100%);
        width: var(--sidebar-width);
    }

    .t2md-main {
        width: 100%;
    }

    .topbar-select {
        max-width: 120px;
    }

    .context-title {
        max-width: 150px;
    }
}

/* ═══════════════════════════════════════════════════
     QD ACTIONS WRAP (contains buttons + inline panel)
  ═══════════════════════════════════════════════════ */
.qd-actions-wrap {
    display: flex;
    flex-direction: column;
    gap: 0;
}

/* ═══════════════════════════════════════════════════
     REFINE BUTTON VARIANT
  ═══════════════════════════════════════════════════ */
.qd-btn.refine {
    background: rgba(168, 85, 247, 0.10);
    color: #a855f7;
    border: 1px solid rgba(168, 85, 247, 0.35);
    gap: 5px;
}

.qd-btn.refine:hover:not(:disabled) {
    background: rgba(168, 85, 247, 0.12);
    color: #a855f7;
    border-color: rgba(168, 85, 247, 0.4);
}

.qd-btn.refine.active {
    background: rgba(168, 85, 247, 0.12);
    color: #a855f7;
    border-color: rgba(168, 85, 247, 0.4);
}

.qd-btn.refine:disabled {
    background: var(--color-surface-2);
    color: var(--color-text-dim);
}

.refine-chevron {
    font-size: 9px;
    transition: transform 0.18s ease;
    margin-left: 1px;
}

/* ═══════════════════════════════════════════════════
     INLINE REFINE PANEL
  ═══════════════════════════════════════════════════ */
.refine-inline-panel {
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.28s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.22s ease;
    opacity: 0;
}

.refine-inline-panel.open {
    max-height: 240px;
    opacity: 1;
}

.refine-panel-inner {
    margin-top: 12px;
    border: 1px solid rgba(168, 85, 247, 0.35);
    border-radius: var(--radius-md);
    background: var(--color-surface-2);
    padding: 10px 12px 10px;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.refine-panel-header {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11.5px;
    font-weight: 600;
    color: #a855f7;
}

.refine-panel-header i {
    font-size: 11px;
    flex-shrink: 0;
}

.refine-optional {
    font-size: 10.5px;
    font-weight: 400;
    color: var(--color-text-dim);
    margin-left: 2px;
}

.refine-panel-textarea {
    width: 100%;
    background: var(--color-surface);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-sm);
    color: var(--color-text);
    font-size: 12.5px;
    font-family: var(--font-sans);
    line-height: 1.55;
    padding: 7px 10px;
    resize: none;
    outline: none;
    box-sizing: border-box;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.refine-panel-textarea:focus {
    border-color: rgba(168, 85, 247, 0.55);
    box-shadow: 0 0 0 2px rgba(168, 85, 247, 0.12);
}

.refine-panel-textarea::placeholder {
    color: var(--color-text-dim);
    font-style: italic;
}

.refine-panel-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}

.refine-kbd-hint {
    font-size: 10.5px;
    color: var(--color-text-dim);
    display: flex;
    align-items: center;
    gap: 3px;
    flex-wrap: wrap;
}

.refine-kbd-hint kbd {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 1px 5px;
    border-radius: 3px;
    border: 1px solid var(--color-border);
    background: var(--color-surface);
    color: var(--color-text-muted);
    font-size: 9.5px;
    font-family: var(--font-mono);
    line-height: 1.5;
}

.refine-panel-actions {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
}

.refine-panel-btn {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 5px 12px;
    border-radius: var(--radius-sm);
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease, opacity 0.15s ease;
    border: none;
}

.refine-panel-btn.cancel {
    background: transparent;
    color: var(--color-text-muted);
    border: 1px solid var(--color-border);
}

.refine-panel-btn.cancel:hover {
    background: var(--color-surface-3);
    color: var(--color-text);
}

.refine-panel-btn.confirm {
    background: rgba(168, 85, 247, 0.15);
    color: #a855f7;
    border: 1px solid rgba(168, 85, 247, 0.35);
}

.refine-panel-btn.confirm:hover:not(:disabled) {
    background: rgba(168, 85, 247, 0.25);
    border-color: rgba(168, 85, 247, 0.55);
}

.refine-panel-btn.confirm:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.disabled-bg-primary {
    background: rgba(44, 124, 229, 0.5) !important;
}

.disabled-bg-danger {
    background: rgb(230, 55, 87, 0.5) !important;
}

.bg-primary {
    background: var(--color-primary);
}

.bg-danger {
    background: var(--color-danger);
}
</style>
<style lang="scss">
@use '@/theme/scss/_user-variables.scss' as *;

body.dark-mode {
    .t2md-sidebar {
        background: $dark-body-bg;
    }

    .messages-container {
        background-color: #15181d !important;
    }

    .input-bar-wrapper {
        background: $dark-body-bg !important;
    }

    .message-avatar.assistant {
        background: $dark-body-bg !important;
    }

    .assistant-bubble {
        background: #282c34 !important
    }

    .qd-btn.save {
        background: $dark-card-bg !important;
        color: $dark-text-secondary !important;
        border: none !important;
    }

    .t2md-topbar {
        background: $dark-body-bg !important;
    }

    .qd-btn.refine {
        background: #210d34;
    }

    .qd-btn.refine:hover:not(:disabled) {
        background: #2c0b4a;
    }

    .qd-btn.refine.active {
        background: #2c0b4a;
    }

    .qd-btn.refine:disabled {
        background: $dark-card-bg !important;
        color: #4b5563 !important;
        border-color: $dark-border-color !important;
        opacity: 0.5;
    }
}
</style>